import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "../api/config";
import RechnungPanel from "./rechnungen/RechnungPanel";
import Ausgangsbuch from "./rechnungen/Ausgangsbuch";
import NenngeldPanel from "./rechnungen/NenngeldPanel";
import Ico from "./rechnungen/Ico";
import "./RechnungenPage.css";

const hasExcel = (card) =>
  (card.attachments || []).some(
    (a) =>
      (a.name || "").toLowerCase().endsWith(".xlsx") ||
      (a.mimeType || "").toLowerCase().includes("spreadsheet") ||
      (a.mimeType || "").toLowerCase().includes("excel")
  );

// Erkennt Lexware-Links + Nummer in der Kartenbeschreibung
const parseLexwareInfo = (desc) => {
  if (!desc) return null;
  // Lexware-URL: quotations = Angebot, invoices = Rechnung/Entwurf
  const urlMatch = desc.match(/https:\/\/app\.lexware\.de\/permalink\/(quotations|invoices)\/view\/([a-f0-9-]+)/i);
  if (!urlMatch) return null;
  const type = urlMatch[1] === "quotations" ? "angebot" : "rechnung";
  const url = urlMatch[0];
  // Nummer: AN1234 oder RE1234
  const nrMatch = desc.match(/\*?\*?(AN\d+|RE\d+)\*?\*?/i);
  const nr = nrMatch ? nrMatch[1].toUpperCase() : null;
  return { type, url, nr };
};

export default function RechnungenPage() {
  const navigate = useNavigate();
  // Welle 119: eigene Rechnungen (aus Mietverwaltung › Dokumente umgezogen) + Ausgangsbuch neben Lexware
  const [suche, setSuche] = useSearchParams();
  // Vertrag/Objekt aus der Mietverwaltung nur EINMAL übernehmen (danach aus der URL nehmen, sonst würde ein
  // Neuladen die gerade bearbeitete Rechnung wieder überschreiben)
  const [start] = useState(() => ({ mv: suche.get("mv") || "", objekt: suche.get("objekt") || "" }));
  // ohne ?tab= zuerst die Auswahl „Rechnung schreiben" oder „Lexware" (tab = "")
  const [tab, setTab] = useState(() => (["schreiben", "buch", "nenngeld", "cards", "archiv", "config"].includes(suche.get("tab"))
    ? suche.get("tab") : "")); // "" | schreiben | buch | nenngeld | cards | archiv | config
  const [rgAuftrag, setRgAuftrag] = useState(null);   // {art: oeffnen|duplizieren, id, n} aus dem Ausgangsbuch
  const [buchStand, setBuchStand] = useState(0);
  const ausBuch = (art) => (id) => { setRgAuftrag({ art, id, n: Date.now() }); setTab("schreiben"); };
  // Reiter in der Adresszeile — nach einem Neuladen bleibt man, wo man war
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (tab) q.set("tab", tab === "form" ? "cards" : tab); else q.delete("tab");
    q.delete("mv"); q.delete("objekt");
    if (q.toString() !== new URLSearchParams(window.location.search).toString()) setSuche(q, { replace: true });
  }, [tab, setSuche]);

  // ── Cards State ─────────────────────────────────────────────────────────
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState("");

  // ── Rechnungs-Formular State ─────────────────────────────────────────────
  const [formCard, setFormCard] = useState(null);  // die ausgewählte Card
  const [formPosten, setFormPosten] = useState([]);
  const [formDatum, setFormDatum] = useState("");
  const [formContactId, setFormContactId] = useState("");
  const [formContactName, setFormContactName] = useState("");
  const [formIntro, setFormIntro] = useState("");
  const [formQrUrl, setFormQrUrl] = useState("");
  const [formSaving, setFormSaving] = useState(false);
  const [formResult, setFormResult] = useState(null);
  const [formError, setFormError] = useState("");

  // ── Config State ─────────────────────────────────────────────────────────
  const [config, setConfig] = useState(null);

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(API_BASE + "/rechnungen/config")
      .then(r => r.json()).then(setConfig).catch(() => {});
  }, []);

  // ── Cards laden ──────────────────────────────────────────────────────────
  const loadCards = useCallback(() => {
    setCardsLoading(true); setCardsError("");
    fetch(API_BASE + "/rechnungen/trello-cards")
      .then(r => { if (!r.ok) throw new Error("Status " + r.status); return r.json(); })
      .then(d => setCards(d.cards || []))
      .catch(e => setCardsError("Fehler: " + e.message))
      .finally(() => setCardsLoading(false));
  }, []);

  useEffect(() => { if (tab === "cards") loadCards(); }, [tab, loadCards]);

  // ── Formular öffnen ───────────────────────────────────────────────────────
  const openForm = useCallback((card) => {
    setFormCard(card);
    setFormDatum(card.parsed?.datum || new Date().toISOString().slice(0, 10));
    setFormContactId(card.lexware_contact_id || "");
    setFormContactName(card.lexware_contact_name || "");
    const desc = card.parsed?.beschreibung || card.title;
    setFormPosten([{ name: desc, description: card.beschreibung || "", menge: 1, einheit: "Pauschal", preis_netto: 0, steuer: 19 }]);
    setFormIntro(`Rechnung für: ${desc}`);
    const att = (card.attachments || []).find(a => a.url);
    setFormQrUrl(att?.url || "");
    setFormResult(null); setFormError("");
  }, []);

  // ── Rechnung erstellen ────────────────────────────────────────────────────
  const createRechnung = useCallback(async () => {
    if (!formCard || !formContactId) { setFormError("Lexware-Kontakt fehlt — Mapping anlegen!"); return; }
    setFormSaving(true); setFormError(""); setFormResult(null);
    try {
      const r = await fetch(API_BASE + "/rechnungen/erstellen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: formCard.card_id,
          card_title: formCard.title,
          datum: formDatum,
          lexware_contact_id: formContactId,
          posten: formPosten,
          introduction: formIntro,
          qr_anhang_url: formQrUrl || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      setFormResult(d);
    } catch (e) { setFormError(e.message); }
    finally { setFormSaving(false); }
  }, [formCard, formContactId, formDatum, formPosten, formIntro, formQrUrl]);

  // ── Archiv State ─────────────────────────────────────────────────────────
  const [archivGroups, setArchivGroups] = useState([]);
  const [archivFolder, setArchivFolder] = useState("");
  const [archivAvailable, setArchivAvailable] = useState(true);
  const [archivLoading, setArchivLoading] = useState(false);
  const [archivError, setArchivError] = useState("");
  const [archivPdfLoading, setArchivPdfLoading] = useState(null); // re_nr oder null
  const [archivPdfResult, setArchivPdfResult] = useState(null);

  const loadArchiv = useCallback(() => {
    setArchivLoading(true); setArchivError(""); setArchivPdfResult(null);
    fetch(API_BASE + "/rechnungen/archiv")
      .then(r => { if (!r.ok) throw new Error("Status " + r.status); return r.json(); })
      .then(d => {
        setArchivGroups(d.groups || []);
        setArchivFolder(d.folder || "");
        setArchivAvailable(d.available !== false);
      })
      .catch(e => setArchivError("Fehler: " + e.message))
      .finally(() => setArchivLoading(false));
  }, []);

  useEffect(() => { if (tab === "archiv") loadArchiv(); }, [tab, loadArchiv]);

  const buildArchivPdf = useCallback(async (re_nr) => {
    setArchivPdfLoading(re_nr); setArchivPdfResult(null);
    try {
      const r = await fetch(API_BASE + "/rechnungen/archiv/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ re_nr }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      setArchivPdfResult(d);
      loadArchiv();
    } catch (e) { setArchivError(e.message); }
    finally { setArchivPdfLoading(null); }
  }, [loadArchiv]);

  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);

  const loadMergePreview = useCallback(() => {
    fetch(API_BASE + "/rechnungen/anhang-merge/preview")
      .then(r => r.json()).then(d => setMergePreview(d)).catch(() => {});
  }, []);

  useEffect(() => { if (tab === "archiv") loadMergePreview(); }, [tab, loadMergePreview]);

  const runMerge = useCallback(async () => {
    setMergeLoading(true); setMergeResult(null);
    try {
      const r = await fetch(API_BASE + "/rechnungen/anhang-merge", { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      setMergeResult(d);
      loadMergePreview();
    } catch (e) { setArchivError(e.message); }
    finally { setMergeLoading(false); }
  }, [loadMergePreview]);


  const [selectedCards, setSelectedCards] = useState(new Set());
  const [activePrefix, setActivePrefix] = useState(null);
  const [anhangModal, setAnhangModal] = useState(false);
  const [anhangRechnungsnummer, setAnhangRechnungsnummer] = useState("");
  const [anhangLoading, setAnhangLoading] = useState(false);
  const [anhangResult, setAnhangResult] = useState(null);
  const [anhangError, setAnhangError] = useState("");

  // ── Schnell-Entwurf Modal ─────────────────────────────────────────────────
  const [schnellModal, setSchnellModal] = useState(null); // null | { card }
  const [schnellContactId, setSchnellContactId] = useState("");       // UUID intern
  const [schnellContactNummer, setSchnellContactNummer] = useState(""); // Kundennr. (Anzeige)
  const [schnellContactName, setSchnellContactName] = useState("");
  const [schnellFreitext, setSchnellFreitext] = useState("");
  const [schnellPosten, setSchnellPosten] = useState([]);  // [{name, menge, einheit, preis_netto, steuer}]
  const [schnellAlsAngebot, setSchnellAlsAngebot] = useState(false);
  const [schnellLoading, setSchnellLoading] = useState(false);
  const [schnellResult, setSchnellResult] = useState(null);
  const [schnellError, setSchnellError] = useState("");

  // ── Angebot aus Excel Modal (Trello) ─────────────────────────────────────
  const [angebotModal, setAngebotModal] = useState(null); // null | { card }
  const [angebotAttachments, setAngebotAttachments] = useState([]);   // xlsx-Anhänge der Karte
  const [angebotAttId, setAngebotAttId] = useState("");              // gewählter Anhang
  const [angebotContactId, setAngebotContactId] = useState("");
  const [angebotContactSearch, setAngebotContactSearch] = useState("");
  const [angebotContactDropdown, setAngebotContactDropdown] = useState(false);
  const [angebotDatum, setAngebotDatum] = useState("");
  const [angebotPreview, setAngebotPreview] = useState(null);        // geparste Items
  const [angebotLoading, setAngebotLoading] = useState(false);
  const [angebotPreviewLoading, setAngebotPreviewLoading] = useState(false);
  const [angebotResult, setAngebotResult] = useState(null);
  const [angebotError, setAngebotError] = useState("");
  // Schema 2 States für Trello-Modal
  const [angebotSheets, setAngebotSheets] = useState([]);
  const [angebotSheet, setAngebotSheet] = useState("");
  const [angebotSheetModal, setAngebotSheetModal] = useState(false);
  const [angebotSchema, setAngebotSchema] = useState("v1");
  const [angebotLosGroups, setAngebotLosGroups] = useState({});
  const [angebotOverwrite, setAngebotOverwrite] = useState(false);
  const [angebotOverwriteSearch, setAngebotOverwriteSearch] = useState("");
  const [angebotOverwriteId, setAngebotOverwriteId] = useState("");
  const [angebotLexAngebote, setAngebotLexAngebote] = useState([]);
  const [angebotOverwriteDropdown, setAngebotOverwriteDropdown] = useState(false);
  const [angebotOverwriteLoading, setAngebotOverwriteLoading] = useState(false);

  // ── Angebot Upload Modal (ohne Trello) ────────────────────────────────────
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);          // File-Objekt
  const [uploadIsDragging, setUploadIsDragging] = useState(false);
  const [uploadContactId, setUploadContactId] = useState("");
  const [uploadContactSearch, setUploadContactSearch] = useState("");
  const [uploadContactDropdown, setUploadContactDropdown] = useState(false);
  const [uploadDatum, setUploadDatum] = useState("");
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadPreviewLoading, setUploadPreviewLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState("");
  // Schema 2 neue States
  const [uploadSheets, setUploadSheets] = useState([]);
  const [uploadSheet, setUploadSheet] = useState("");
  const [uploadSheetLoading, setUploadSheetLoading] = useState(false);
  const [uploadSheetModal, setUploadSheetModal] = useState(false);
  const [uploadSchema, setUploadSchema] = useState("v1");
  const [uploadLosGroups, setUploadLosGroups] = useState({}); // {group_key: bool}
  const [uploadOverwrite, setUploadOverwrite] = useState(false);
  const [uploadOverwriteSearch, setUploadOverwriteSearch] = useState("");
  const [uploadOverwriteId, setUploadOverwriteId] = useState("");
  const [uploadLexAngebote, setUploadLexAngebote] = useState([]);
  const [uploadOverwriteDropdown, setUploadOverwriteDropdown] = useState(false);
  const [uploadOverwriteLoading, setUploadOverwriteLoading] = useState(false);

  // ── Fremdes Excel → Schema 2 (KI-Prompt zum Kopieren + manuelles Mapping) ──
  const [fremdModal, setFremdModal] = useState(null); // null | { source: {file} | {card_id, attachment_id}, sheet }

  // Abschlussblöcke: Trello-Modal
  const DEFAULT_ZAHLUNGEN = [
    "25% bei Auftragserteilung",
    "40% bei Lieferung",
    "10% mit Beginn der Dacheindeckung",
    "10% mit Beginn der Arbeiten am Wintergarten-Lichtband",
    "10% mit Beginn der Innenarbeiten Veluxfenster",
    "Rest bei Fertigstellung",
  ];
  const [angebotZusammenfassung, setAngebotZusammenfassung] = useState(true);
  const [angebotZahlungen, setAngebotZahlungen] = useState(false);
  const [angebotZahlungsTexte, setAngebotZahlungsTexte] = useState([...DEFAULT_ZAHLUNGEN]);
  const [angebotAusfuehrung, setAngebotAusfuehrung] = useState(false);
  const [angebotAusfuehrungText, setAngebotAusfuehrungText] = useState("");
  // Abschlussblöcke: Upload-Modal
  const [uploadZusammenfassung, setUploadZusammenfassung] = useState(true);
  const [uploadZahlungen, setUploadZahlungen] = useState(false);
  const [uploadZahlungsTexte, setUploadZahlungsTexte] = useState([...DEFAULT_ZAHLUNGEN]);
  const [uploadAusfuehrung, setUploadAusfuehrung] = useState(false);
  const [uploadAusfuehrungText, setUploadAusfuehrungText] = useState("");

  // Lexware Standardtexte (Freitext-Vorlagen) – für Angebot-Preview
  const [textVorlagen, setTextVorlagen] = useState([]);
  const [textVorlagenLoading, setTextVorlagenLoading] = useState(false);
  const [selectedTextVorlage, setSelectedTextVorlage] = useState("");
  // Eingefügte Standardtexte: [{id: uuid, text: str, insertAfter: number|"intro"|"end"}]
  const [insertedTexts, setInsertedTexts] = useState([]);
  const [insertPosition, setInsertPosition] = useState("end"); // "intro"|"end"|number

  // Kontakt-Suche Dropdown (wird von Angebot-Modal + Schnellrechnung geteilt)
  const [allContacts, setAllContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);

  // ── Einzel-Anhang Modal ──────────────────────────────────────────────────
  const [einzelAnhangModal, setEinzelAnhangModal] = useState(false);
  const [einzelAnhangCard, setEinzelAnhangCard] = useState(null);
  const [einzelAnhangRE, setEinzelAnhangRE] = useState("");
  const [einzelAnhangLoading, setEinzelAnhangLoading] = useState(false);
  const [einzelAnhangError, setEinzelAnhangError] = useState("");
  const [einzelAnhangResult, setEinzelAnhangResult] = useState(null);
  const [einzelAnhangConflict, setEinzelAnhangConflict] = useState(null); // { existing_file }

  const openEinzelAnhangModal = useCallback((card) => {
    setEinzelAnhangCard(card);
    setEinzelAnhangRE(card.parsed?.re_nr || "");
    setEinzelAnhangLoading(false);
    setEinzelAnhangError("");
    setEinzelAnhangResult(null);
    setEinzelAnhangConflict(null);
    setEinzelAnhangModal(true);
  }, []);

  const createEinzelAnhang = useCallback(async (conflictAction = null) => {
    if (!einzelAnhangRE.trim() || !einzelAnhangCard) return;
    setEinzelAnhangLoading(true);
    setEinzelAnhangError("");
    setEinzelAnhangConflict(null);
    try {
      const r = await fetch(API_BASE + "/rechnungen/anhang-einzel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rechnungsnummer: einzelAnhangRE.trim(),
          card_id: einzelAnhangCard.card_id,
          conflict_action: conflictAction,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      if (d.conflict) {
        setEinzelAnhangConflict({ existing_file: d.existing_file });
      } else {
        setEinzelAnhangResult(d);
      }
    } catch (e) {
      setEinzelAnhangError(e.message);
    } finally {
      setEinzelAnhangLoading(false);
    }
  }, [einzelAnhangRE, einzelAnhangCard]);

  const openAngebotModal = useCallback(async (card) => {
    setAngebotModal({ card });
    setAngebotAttachments([]);
    setAngebotAttId("");
    setAngebotContactId("");
    setAngebotContactSearch("");
    setAngebotContactDropdown(false);
    setAngebotDatum(card.parsed?.datum || new Date().toISOString().slice(0, 10));
    setAngebotPreview(null);
    setAngebotResult(null);
    setAngebotError("");
    setAngebotSheets([]);
    setAngebotSheet("");
    setAngebotSheetModal(false);
    setAngebotSchema("v1");
    setAngebotLosGroups({});
    setAngebotOverwrite(false);
    setAngebotOverwriteSearch("");
    setAngebotOverwriteId("");
    setAngebotLexAngebote([]);
    setAngebotOverwriteDropdown(false);

    // xlsx-Anhänge laden
    try {
      const r = await fetch(`${API_BASE}/rechnungen/trello-excel-anhaenge/${card.card_id}`);
      if (r.ok) {
        const d = await r.json();
        setAngebotAttachments(d.attachments || []);
        if (d.attachments?.length === 1) setAngebotAttId(d.attachments[0].id);
      }
    } catch (_) {}

    // Kontakte laden
    if (!contactsLoaded) {
      try {
        const r = await fetch(`${API_BASE}/rechnungen/lexware-contacts`);
        if (r.ok) {
          const d = await r.json();
          setAllContacts(d.contacts || []);
          setContactsLoaded(true);
        }
      } catch (_) {}
    }
  }, [contactsLoaded]);

  const loadAngebotSheets = useCallback(async (cardId, attId) => {
    if (!cardId || !attId) return;
    try {
      const r = await fetch(`${API_BASE}/rechnungen/angebot-trello-sheets/${cardId}/${attId}`);
      if (r.ok) {
        const d = await r.json();
        setAngebotSheets(d.sheets || []);
        setAngebotSheet(d.default_sheet || "");
        setAngebotSchema(d.detected_schema || "v1");
        if ((d.sheets || []).length > 1) setAngebotSheetModal(true);
      }
    } catch (_) {}
  }, []);

  const loadAngebotLexAngebote = useCallback(async (search = "") => {
    setAngebotOverwriteLoading(true);
    try {
      const r = await fetch(`${API_BASE}/rechnungen/lexoffice-angebote?search=${encodeURIComponent(search)}&size=25`);
      if (r.ok) {
        const d = await r.json();
        setAngebotLexAngebote(d.angebote || []);
      }
    } catch (_) {}
    finally { setAngebotOverwriteLoading(false); }
  }, []);

  const angebotFilteredContacts = useMemo(() => {
    const q = angebotContactSearch.trim().toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter(c => {
      const nr = String(c.nummer ?? "").toLowerCase();
      const name = (c.name || "").toLowerCase();
      return nr.includes(q) || name.includes(q);
    });
  }, [allContacts, angebotContactSearch]);

  const loadAngebotPreview = useCallback(async () => {
    if (!angebotModal || !angebotAttId || !angebotContactId) return;
    setAngebotPreviewLoading(true); setAngebotError("");
    try {
      const r = await fetch(`${API_BASE}/rechnungen/angebot-excel-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: angebotModal.card.card_id,
          attachment_id: angebotAttId,
          lexware_contact_id: angebotContactId,
          datum: angebotDatum,
          sheet_name: angebotSheet || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      setAngebotPreview(d);
      if (d.schema) setAngebotSchema(d.schema);
      if (d.schema === "v2" && d.parsed?.ungrouped_artikel?.length > 0) {
        const init = {};
        d.parsed.ungrouped_artikel.forEach(g => { init[g.group_key] = null; });
        setAngebotLosGroups(init);
      }
      // Standardtexte laden (einmalig)
      if (textVorlagen.length === 0 && !textVorlagenLoading) {
        setTextVorlagenLoading(true);
        fetch(`${API_BASE}/rechnungen/lexware-text-templates`)
          .then(r => r.json())
          .then(d => setTextVorlagen(d.templates || []))
          .catch(() => {})
          .finally(() => setTextVorlagenLoading(false));
      }
    } catch (e) { setAngebotError(e.message); }
    finally { setAngebotPreviewLoading(false); }
  }, [angebotModal, angebotAttId, angebotContactId, angebotDatum, angebotSheet]);

  const createAngebot = useCallback(async () => {
    if (!angebotModal || !angebotAttId || !angebotContactId) return;
    setAngebotLoading(true); setAngebotError("");
    try {
      const losCreate = (() => {
        const byNum = {};
        Object.entries(angebotLosGroups).forEach(([gk, num]) => {
          if (typeof num !== "number" || !num) return;
          if (!byNum[num]) byNum[num] = [];
          byNum[num].push(gk);
        });
        return Object.keys(byNum).sort((a, b) => a - b).map(n => byNum[n]);
      })();
      // Excel-LOS Zuweisungen: ex_A → { "A": ["gk1", ...] }
      const losExcelAppend = (() => {
        const result = {};
        Object.entries(angebotLosGroups).forEach(([gk, val]) => {
          if (typeof val === "string" && val.startsWith("ex_")) {
            const lbl = val.replace("ex_", "");
            if (!result[lbl]) result[lbl] = [];
            result[lbl].push(gk);
          }
        });
        return Object.keys(result).length > 0 ? result : undefined;
      })();
      const r = await fetch(`${API_BASE}/rechnungen/angebot-aus-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: angebotModal.card.card_id,
          attachment_id: angebotAttId,
          lexware_contact_id: angebotContactId,
          datum: angebotDatum,
          sheet_name: angebotSheet || undefined,
          overwrite_quotation_id: angebotOverwrite && angebotOverwriteId ? angebotOverwriteId : undefined,
          los_groups_create: losCreate.length > 0 ? losCreate : undefined,
          los_excel_append: losExcelAppend,
          inserted_texts: insertedTexts.length > 0 ? insertedTexts : undefined,
          include_zusammenfassung: angebotZusammenfassung,
          zahlungsmodalitaeten: angebotZahlungen ? angebotZahlungsTexte : undefined,
          ausfuehrungszeit: angebotAusfuehrung && angebotAusfuehrungText ? angebotAusfuehrungText : undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      setAngebotResult(d);
    } catch (e) { setAngebotError(e.message); }
    finally { setAngebotLoading(false); }
  }, [angebotModal, angebotAttId, angebotContactId, angebotDatum, angebotSheet, angebotOverwrite, angebotOverwriteId, angebotLosGroups, insertedTexts, angebotZusammenfassung, angebotZahlungen, angebotZahlungsTexte, angebotAusfuehrung, angebotAusfuehrungText]);

  // ── Upload-Angebot Callbacks ──────────────────────────────────────────────
  const openUploadModal = useCallback(async () => {
    setUploadModal(true);
    setUploadFile(null);
    setUploadContactId("");
    setUploadContactSearch("");
    setUploadContactDropdown(false);
    setUploadDatum(new Date().toISOString().slice(0, 10));
    setUploadPreview(null);
    setUploadResult(null);
    setUploadError("");
    setUploadSheets([]);
    setUploadSheet("");
    setUploadSheetModal(false);
    setUploadSchema("v1");
    setUploadLosGroups({});
    setUploadOverwrite(false);
    setUploadOverwriteSearch("");
    setUploadOverwriteId("");
    setUploadLexAngebote([]);
    setUploadOverwriteDropdown(false);
    // Kontakte voraufladen
    if (!contactsLoaded) {
      try {
        const r = await fetch(`${API_BASE}/rechnungen/lexware-contacts`);
        if (r.ok) {
          const d = await r.json();
          setAllContacts(d.contacts || []);
          setContactsLoaded(true);
        }
      } catch (_) {}
    }
  }, [contactsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadFile = useCallback(async (f) => {
    if (!f) return;
    setUploadFile(f);
    setUploadPreview(null);
    setUploadError("");
    setUploadSheets([]);
    setUploadSheet("");
    setUploadSchema("v1");
    setUploadLosGroups({});
    // Sheets + Schema erkennen
    setUploadSheetLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API_BASE}/rechnungen/angebot-excel-sheets`, { method: "POST", body: fd });
      const d = await r.json();
      if (r.ok) {
        setUploadSheets(d.sheets || []);
        setUploadSheet(d.default_sheet || "");
        setUploadSchema(d.detected_schema || "v1");
        if ((d.sheets || []).length > 1) setUploadSheetModal(true);
      }
    } catch (_) {}
    finally { setUploadSheetLoading(false); }
  }, []);

  const loadUploadPreview = useCallback(async () => {
    if (!uploadFile) return;
    setUploadPreviewLoading(true); setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (uploadDatum) fd.append("datum", uploadDatum);
      if (uploadSheet) fd.append("sheet_name", uploadSheet);
      const r = await fetch(`${API_BASE}/rechnungen/angebot-excel-upload-preview`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setUploadPreview(d);
      if (d.schema) setUploadSchema(d.schema);
      if (d.schema === "v2" && d.parsed?.ungrouped_artikel?.length > 0) {
        const init = {};
        d.parsed.ungrouped_artikel.forEach(g => { init[g.group_key] = null; });
        setUploadLosGroups(init);
      }
      // Standardtexte laden (einmalig)
      if (textVorlagen.length === 0 && !textVorlagenLoading) {
        setTextVorlagenLoading(true);
        fetch(`${API_BASE}/rechnungen/lexware-text-templates`)
          .then(r => r.json())
          .then(d => setTextVorlagen(d.templates || []))
          .catch(() => {})
          .finally(() => setTextVorlagenLoading(false));
      }
    } catch (e) { setUploadError(e.message); }
    finally { setUploadPreviewLoading(false); }
  }, [uploadFile, uploadDatum, uploadSheet]);

  const loadUploadLexAngebote = useCallback(async (search = "") => {
    setUploadOverwriteLoading(true);
    try {
      const r = await fetch(`${API_BASE}/rechnungen/lexoffice-angebote?search=${encodeURIComponent(search)}&size=25`);
      if (r.ok) {
        const d = await r.json();
        setUploadLexAngebote(d.angebote || []);
      }
    } catch (_) {}
    finally { setUploadOverwriteLoading(false); }
  }, []);

  const createUploadAngebot = useCallback(async () => {
    if (!uploadFile || !uploadContactId) return;
    setUploadLoading(true); setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("lexware_contact_id", uploadContactId);
      if (uploadDatum) fd.append("datum", uploadDatum);
      if (uploadSheet) fd.append("sheet_name", uploadSheet);
      if (uploadOverwrite && uploadOverwriteId) fd.append("overwrite_quotation_id", uploadOverwriteId);
      // LOS-Gruppen: grouped by number → [[gk1, gk2], [gk3]]
      const losCreate = (() => {
        const byNum = {};
        Object.entries(uploadLosGroups).forEach(([gk, num]) => {
          if (typeof num !== "number" || !num) return;
          if (!byNum[num]) byNum[num] = [];
          byNum[num].push(gk);
        });
        return Object.keys(byNum).sort((a, b) => a - b).map(n => byNum[n]);
      })();
      // Excel-LOS Zuweisungen: ex_A → { "A": ["gk1", ...] }
      const losExcelAppend = (() => {
        const result = {};
        Object.entries(uploadLosGroups).forEach(([gk, val]) => {
          if (typeof val === "string" && val.startsWith("ex_")) {
            const lbl = val.replace("ex_", "");
            if (!result[lbl]) result[lbl] = [];
            result[lbl].push(gk);
          }
        });
        return Object.keys(result).length > 0 ? result : null;
      })();
      if (losCreate.length > 0) fd.append("los_groups_create", JSON.stringify(losCreate));
      if (losExcelAppend) fd.append("los_excel_append", JSON.stringify(losExcelAppend));
      if (insertedTexts.length > 0) fd.append("inserted_texts", JSON.stringify(insertedTexts));
      fd.append("include_zusammenfassung", String(uploadZusammenfassung));
      if (uploadZahlungen && uploadZahlungsTexte.length > 0) fd.append("zahlungsmodalitaeten", JSON.stringify(uploadZahlungsTexte));
      if (uploadAusfuehrung && uploadAusfuehrungText) fd.append("ausfuehrungszeit", uploadAusfuehrungText);
      const r = await fetch(`${API_BASE}/rechnungen/excel-angebot-erstellen`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setUploadResult(d);
    } catch (e) { setUploadError(e.message); }
    finally { setUploadLoading(false); }
  }, [uploadFile, uploadContactId, uploadDatum, uploadSheet, uploadOverwrite, uploadOverwriteId, uploadLosGroups, insertedTexts, uploadZusammenfassung, uploadZahlungen, uploadZahlungsTexte, uploadAusfuehrung, uploadAusfuehrungText]);

  const uploadFilteredContacts = useMemo(() => {
    const q = uploadContactSearch.trim().toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter(c => {
      const nr = String(c.nummer ?? "").toLowerCase();
      const name = (c.name || "").toLowerCase();
      return nr.includes(q) || name.includes(q);
    });
  }, [allContacts, uploadContactSearch]);

  // Kontakt-Suche Dropdown
  const loadAllContacts = useCallback(async () => {
    if (contactsLoaded) return;
    try {
      const r = await fetch(`${API_BASE}/rechnungen/lexware-contacts`);
      if (r.ok) {
        const d = await r.json();
        setAllContacts(d.contacts || []);
        setContactsLoaded(true);
      }
    } catch (_) {}
  }, [contactsLoaded]);

  const selectContact = useCallback((contact) => {
    setSchnellContactId(contact.id);
    setSchnellContactNummer(contact.nummer || "");
    setSchnellContactName(contact.name || "");
    setContactSearch(`${contact.nummer ? contact.nummer + " \u2013 " : ""}${contact.name}`);
    setContactDropdownOpen(false);
  }, []);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter(c => {
      const nr = String(c.nummer ?? "").toLowerCase();
      const name = (c.name || "").toLowerCase();
      return nr.includes(q) || name.includes(q);
    });
  }, [allContacts, contactSearch]);

  const openSchnellModal = useCallback(async (card) => {
    const prefix = card.parsed?.prefix || "";
    setSchnellModal({ card });
    setSchnellContactId("");
    setSchnellContactNummer("");
    setSchnellContactName("");
    setContactSearch("");
    setContactDropdownOpen(false);
    setSchnellFreitext("");
    setSchnellAlsAngebot(false);
    setSchnellResult(null);
    setSchnellError("");

    // Checklisten-Positionen aus Karte laden
    const allChecklists = card.checklists || [];
    const rechnungChecklist = allChecklists.find(cl =>
      cl.name.toLowerCase().includes("rechnung")
    ) || allChecklists[0];
    if (rechnungChecklist && rechnungChecklist.items?.length > 0) {
      setSchnellPosten(rechnungChecklist.items.map(item => ({
        name: item.name,
        menge: item.menge || 1,
        einheit: "Stück",
        preis_netto: 0,
        steuer: 19,
      })));
    } else {
      setSchnellPosten([]);
    }

    // Kontakte laden (einmalig gecacht)
    let contacts = allContacts;
    if (!contactsLoaded) {
      try {
        const r = await fetch(`${API_BASE}/rechnungen/lexware-contacts`);
        if (r.ok) {
          const d = await r.json();
          contacts = d.contacts || [];
          setAllContacts(contacts);
          setContactsLoaded(true);
        }
      } catch (_) {}
    }

    // Objekt-Info + Auto-Match
    if (prefix) {
      try {
        const r = await fetch(`${API_BASE}/rechnungen/objekt-info/${encodeURIComponent(prefix)}`);
        if (r.ok) {
          const d = await r.json();
          setSchnellFreitext(`Objekt ${prefix}${d.bezeichnung ? ", " + d.bezeichnung : ""}`);
          if (d.lexware_contact_id) {
            const found = contacts.find(c => c.id === d.lexware_contact_id);
            const nr = String(found?.nummer ?? "");
            setSchnellContactId(d.lexware_contact_id);
            setSchnellContactNummer(nr);
            setSchnellContactName(d.name || "");
            setContactSearch(`${nr ? nr + " \u2013 " : ""}${d.name || ""}`);
          } else {
            // Fallback: Prefix als Wort im Kontaktnamen suchen
            // z.B. "Ingvar Holthey 023" → Prefix "023" trifft zu
            const prefixNorm = prefix.replace(/^0+/, ""); // "023" → "23"
            const nameMatches = contacts.filter(c => {
              const words = (c.name || "").split(/[\s\-_/]+/);
              return words.some(w => w === prefix || w === prefixNorm);
            });
            if (nameMatches.length === 1) {
              const m = nameMatches[0];
              const nr = String(m.nummer ?? "");
              setSchnellContactId(m.id);
              setSchnellContactNummer(nr);
              setSchnellContactName(m.name || "");
              setContactSearch(`${nr ? nr + " \u2013 " : ""}${m.name}`);
            }
          }
        }
      } catch (_) {}
    }
  }, [allContacts, contactsLoaded]);

  const createSchnellEntwurf = useCallback(async () => {
    if (!schnellContactId.trim() || !schnellModal) return;
    setSchnellLoading(true); setSchnellError(""); setSchnellResult(null);
    try {
      const r = await fetch(API_BASE + "/rechnungen/schnell-entwurf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: schnellModal.card.card_id,
          prefix: schnellModal.card.parsed?.prefix || "",
          lexware_contact_id: schnellContactId.trim(),
          freitext: schnellFreitext.trim(),
          posten: schnellPosten.length > 0 ? schnellPosten : null,
          als_angebot: schnellAlsAngebot,
        }),
      });
      let d;
      try { d = await r.json(); } catch { d = {}; }
      if (!r.ok) throw new Error(d.detail || `Serverfehler ${r.status}`);
      setSchnellResult(d);
    } catch (e) { setSchnellError(e.message); }
    finally { setSchnellLoading(false); }
  }, [schnellModal, schnellContactId, schnellFreitext, schnellPosten, schnellAlsAngebot]);


  const groupedCards = useMemo(() => {
    const groups = {};
    for (const card of cards) {
      const prefix = card.parsed?.prefix || "–";
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push(card);
    }
    // Innerhalb jeder Gruppe: Excel-Karten zuerst
    for (const prefix in groups) {
      groups[prefix].sort((a, b) => (hasExcel(b) ? 1 : 0) - (hasExcel(a) ? 1 : 0));
    }
    // Gruppen sortieren: Gruppen mit mind. einer Excel-Karte zuerst, dann alphabetisch
    return Object.entries(groups).sort(([a, cardsA], [b, cardsB]) => {
      const aHasExcel = cardsA.some(hasExcel) ? 1 : 0;
      const bHasExcel = cardsB.some(hasExcel) ? 1 : 0;
      if (bHasExcel !== aHasExcel) return bHasExcel - aHasExcel;
      return a.localeCompare(b);
    });
  }, [cards]);

  const selectedImageCount = useMemo(() => {
    return cards
      .filter(c => selectedCards.has(c.card_id))
      .reduce((sum, c) => sum + (c.attachments?.filter(a => a.mimeType?.startsWith("image/")).length || 0), 0);
  }, [cards, selectedCards]);

  // ── Checkbox Toggle ───────────────────────────────────────────────────────
  const toggleCard = useCallback((card) => {
    const prefix = card.parsed?.prefix || "–";
    setSelectedCards(prev => {
      const next = new Set(prev);
      if (next.has(card.card_id)) {
        next.delete(card.card_id);
        if (next.size === 0) setActivePrefix(null);
      } else {
        next.add(card.card_id);
        setActivePrefix(prefix);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCards(new Set());
    setActivePrefix(null);
  }, []);

  // ── Anhang-PDF erstellen ──────────────────────────────────────────────────
  const createAnhang = useCallback(async () => {
    if (!anhangRechnungsnummer.trim()) return;
    setAnhangLoading(true); setAnhangError(""); setAnhangResult(null);
    try {
      const r = await fetch(API_BASE + "/rechnungen/anhang-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rechnungsnummer: anhangRechnungsnummer.trim(),
          card_ids: Array.from(selectedCards),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Status " + r.status);
      setAnhangResult(d);
      clearSelection();
    } catch (e) { setAnhangError(e.message); }
    finally { setAnhangLoading(false); }
  }, [anhangRechnungsnummer, selectedCards, clearSelection]);


  const updatePosten = (i, field, val) => {
    setFormPosten(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));
  };
  const addPosten = () => setFormPosten(prev => [...prev, { name: "", description: "", menge: 1, einheit: "Stück", preis_netto: 0, steuer: 19 }]);
  const removePosten = (i) => setFormPosten(prev => prev.filter((_, idx) => idx !== i));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="r-page">
      <div className="r-header">
        <button className="r-back" onClick={() => (tab ? setTab("") : navigate("/"))}><Ico n="links" /> {tab ? "Auswahl" : "Zurück"}</button>
        <h1>Rechnungen</h1>
        <span className="r-header-platz" />
      </div>

      {/* Auswahl beim Öffnen: eigene Rechnungen oder Lexware */}
      {!tab && (
        <div className="r-start">
          <button className="r-start-karte" onClick={() => setTab("schreiben")}>
            <span className="r-start-titel">Rechnung schreiben</span>
            <span className="r-start-text">Eigene Rechnungen und Gutschriften aus der Datenbank — mit Ausgangsbuch, Nummernkreisen, Google-Positionen und Arbeitszeit.</span>
          </button>
          <button className="r-start-karte" onClick={() => setTab("cards")}>
            <span className="r-start-titel">Lexware</span>
            <span className="r-start-text">Trello-Karten mit dem Label „{config?.rechnung_label || "Rechnung schreiben"}“ → Rechnungen und Angebote in Lexware, Anhang-Archiv.</span>
          </button>
        </div>
      )}

      {/* Reiter mittig, ohne Icons — links eigene Rechnungen, rechts Lexware */}
      {tab && (
        <div className="r-tabs-zeile">
          <div className="r-tabs">
            <button className={tab === "schreiben" ? "active" : ""} onClick={() => setTab("schreiben")}>Rechnung schreiben</button>
            <button className={tab === "buch" ? "active" : ""} onClick={() => setTab("buch")}>Ausgangsbuch</button>
            <button className={tab === "nenngeld" ? "active" : ""} onClick={() => setTab("nenngeld")}>Nenngeld</button>
            <span className="r-tabs-trenner" />
            <button className={tab === "cards" || tab === "form" ? "active" : ""} onClick={() => setTab("cards")}>Lexware</button>
            <button className={tab === "archiv" ? "active" : ""} onClick={() => setTab("archiv")}>Anhang-Archiv</button>
            <button className={tab === "config" ? "active" : ""} onClick={() => setTab("config")}>Konfiguration</button>
          </div>
        </div>
      )}

      {/* ═══════════════ Welle 119: eigene Rechnungen ═══════════════ */}
      <div style={{ display: tab === "schreiben" ? "block" : "none" }} className="rg-seite">
        <RechnungPanel mietvertragNr={start.mv} objekt={start.objekt} auftrag={rgAuftrag}
          onBuchGeaendert={() => setBuchStand((x) => x + 1)} />
      </div>
      {tab === "nenngeld" && (
        <div className="rg-seite">
          <NenngeldPanel onBuchGeaendert={() => setBuchStand((x) => x + 1)} />
        </div>
      )}
      {tab === "buch" && (
        <div className="rg-seite">
          <Ausgangsbuch neuLaden={buchStand} onOeffnen={ausBuch("oeffnen")} onDuplizieren={ausBuch("duplizieren")} />
        </div>
      )}

      {/* ═══════════════ TAB: CARDS ═══════════════ */}
      {tab === "cards" && (
        <div className="r-section">
          <div className="r-section-header">
            <h2>Trello-Cards <span className="r-label-hint">Label: "{config?.rechnung_label || "Rechnung schreiben"}"</span></h2>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="r-btn angebot-excel" onClick={openUploadModal}><Ico n="diagramm" /> Angebot erstellen</button>
              <button className="r-btn primary" onClick={loadCards} disabled={cardsLoading}>
                {cardsLoading ? "Lädt..." : <><Ico n="aktualisieren" /> Aktualisieren</>}
              </button>
            </div>
          </div>
          {cardsError && <div className="r-error">{cardsError}</div>}
          {!config?.trello && (
            <div className="r-warning"><Ico n="warnung" /> Trello-Credentials fehlen — bitte TRELLO_API_KEY, TRELLO_TOKEN, TRELLO_BOARD_ID in .env setzen.</div>
          )}
          {cards.length === 0 && !cardsLoading && (
            <div className="r-empty">Keine Cards mit Label "{config?.rechnung_label}" gefunden.</div>
          )}
          <div className="r-card-list">
            {groupedCards.map(([prefix, groupCards]) => {
              const groupImgs = groupCards.reduce((s, c) => s + (c.attachments?.filter(a => a.mimeType?.startsWith("image/")).length || 0), 0);
              const isLocked = activePrefix !== null && activePrefix !== prefix;
              return (
                <div key={prefix} className={`r-prefix-group ${isLocked ? "locked" : ""}`}>
                  <div className="r-prefix-header">
                    <span className="r-prefix-label">{prefix}</span>
                    <span className="r-prefix-count">{groupCards.length} Karte{groupCards.length !== 1 ? "n" : ""} · <Ico n="klammer" /> {groupImgs} Bild{groupImgs !== 1 ? "er" : ""}</span>
                  </div>
                  {groupCards.map(card => {
                    const checked = selectedCards.has(card.card_id);
                    const disabled = isLocked;
                    const imgCount = card.attachments?.filter(a => a.mimeType?.startsWith("image/")).length || 0;
                    const lexware = parseLexwareInfo(card.beschreibung);
                    return (
                      <div key={card.card_id} className={`r-card ${checked ? "selected" : ""} ${disabled ? "disabled" : ""} ${lexware ? "has-lexware" : ""}`}>
                        <label className="r-card-checkbox" title={disabled ? `Nur ${activePrefix}-Karten wählbar` : ""}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => !disabled && toggleCard(card)}
                          />
                        </label>
                        <div className="r-card-main" onClick={() => !disabled && toggleCard(card)} style={{ cursor: "pointer" }}>
                          <div className="r-card-title">{card.parsed?.beschreibung || card.title}</div>
                          <div className="r-card-meta">
                            {card.parsed?.datum && <span className="r-tag date">{card.parsed.datum}</span>}
                            {imgCount > 0 && <span className="r-tag attach"><Ico n="bild" /> {imgCount}</span>}
                            {lexware && (
                              <span className="r-tag lexware-done">
                                <Ico n={lexware.type === "angebot" ? "diagramm" : "beleg"} /> {lexware.nr || (lexware.type === "angebot" ? "Angebot" : "Rechnung")} erstellt
                              </span>
                            )}
                          </div>
                          {card.beschreibung && (
                            <div className="r-card-desc">{card.beschreibung.slice(0, 120)}{card.beschreibung.length > 120 ? "…" : ""}</div>
                          )}
                        </div>
                        <div className="r-card-actions" onClick={e => e.stopPropagation()}>
                          <a href={card.card_url} target="_blank" rel="noreferrer" className="r-btn ghost small">Trello</a>
                          <button className="r-btn ghost small" onClick={() => openEinzelAnhangModal(card)}><Ico n="klammer" /> Anhang erstellen</button>
                          {lexware && (
                            <a
                              href={lexware.url}
                              target="_blank"
                              rel="noreferrer"
                              className={`r-btn small ${lexware.type === "angebot" ? "angebot-excel" : "primary"}`}
                            >
                              <Ico n={lexware.type === "angebot" ? "diagramm" : "beleg"} /> {lexware.type === "angebot" ? "Angebot öffnen" : "Rechnung öffnen"}
                            </a>
                          )}
                          {hasExcel(card) ? (
                            <button className="r-btn angebot-excel small" onClick={() => openAngebotModal(card)}>
                              <Ico n="diagramm" /> {lexware ? "Neu erstellen" : "Angebot erstellen"}
                            </button>
                          ) : (
                            card.parsed?.prefix && (
                              <button className="r-btn accent small" onClick={() => openSchnellModal(card)}>
                                <Ico n="dokument" /> {lexware ? "Neu erstellen" : "Entwurf"}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Sticky-Bar: Auswahl + Anhang-Button */}
          {selectedCards.size > 0 && (
            <div className="r-sticky-bar">
              <div className="r-sticky-info">
                <strong>{selectedCards.size}</strong> Karte{selectedCards.size !== 1 ? "n" : ""} ausgewählt
                {selectedImageCount > 0 && <span> · <Ico n="bild" /> {selectedImageCount} Bild{selectedImageCount !== 1 ? "er" : ""}</span>}
                <span className="r-sticky-prefix"> (Prefix: {activePrefix})</span>
              </div>
              <div className="r-sticky-actions">
                <button className="r-btn ghost small" onClick={clearSelection}>Auswahl aufheben</button>
                <button className="r-btn primary" onClick={() => { setAnhangModal(true); setAnhangResult(null); setAnhangError(""); }}>
                  <Ico n="klammer" /> Anhang-PDF erstellen
                </button>
              </div>
            </div>
          )}

          {/* Anhang-Modal */}
          {anhangModal && (
            <div className="r-modal-overlay" onClick={() => !anhangLoading && setAnhangModal(false)}>
              <div className="r-modal" onClick={e => e.stopPropagation()}>
                <div className="r-modal-header">
                  <h3><Ico n="klammer" /> Anhang-PDF erstellen</h3>
                  {!anhangLoading && <button className="r-modal-close" onClick={() => setAnhangModal(false)}><Ico n="schliessen" /></button>}
                </div>

                {!anhangResult ? (
                  <>
                    <div className="r-modal-info">
                      <div className="r-modal-summary">
                        <strong>{selectedCards.size}</strong> Karte{selectedCards.size !== 1 ? "n" : ""}
                        {" · "}
                        <strong>{selectedImageCount}</strong> Bild{selectedImageCount !== 1 ? "er" : ""}
                        {" · Prefix: "}<strong>{activePrefix}</strong>
                      </div>
                      <div className="r-modal-cards">
                        {cards.filter(c => selectedCards.has(c.card_id)).map(c => (
                          <div key={c.card_id} className="r-modal-card-item">
                            <span><Ico n="dokument" /> {c.parsed?.beschreibung || c.title}</span>
                            <span className="subtle"> ({c.attachments?.filter(a => a.mimeType?.startsWith("image/")).length || 0} Bilder)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="r-form-row">
                      <label>Rechnungsnummer <span className="r-hint-inline">(aus Lexware)</span></label>
                      <input
                        autoFocus
                        value={anhangRechnungsnummer}
                        onChange={e => setAnhangRechnungsnummer(e.target.value)}
                        placeholder="z.B. RE2026-0042"
                        onKeyDown={e => e.key === "Enter" && !anhangLoading && anhangRechnungsnummer.trim() && createAnhang()}
                      />
                    </div>
                    {anhangError && <div className="r-error">{anhangError}</div>}
                    <div className="r-modal-actions">
                      <button className="r-btn ghost" onClick={() => setAnhangModal(false)} disabled={anhangLoading}>Abbrechen</button>
                      <button
                        className="r-btn primary"
                        onClick={createAnhang}
                        disabled={anhangLoading || !anhangRechnungsnummer.trim()}
                      >
                        {anhangLoading ? "Erstelle PDF..." : <><Ico n="klammer" /> PDF erstellen</>}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="r-modal-success">
                    <div className="r-success-icon"><Ico n="haken" gr={40} /></div>
                    <div><strong>PDF gespeichert!</strong></div>
                    <div className="r-modal-filename">{anhangResult.filename}</div>
                    <div className="subtle">{anhangResult.total_images} Bild{anhangResult.total_images !== 1 ? "er" : ""} aus {anhangResult.cards} Karte{anhangResult.cards !== 1 ? "n" : ""}</div>
                    <div className="r-modal-path subtle">{anhangResult.path}</div>
                    <button className="r-btn primary" onClick={() => { setAnhangModal(false); setAnhangRechnungsnummer(""); }}>Schließen</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ EINZEL-ANHANG MODAL ═══════════════ */}
      {einzelAnhangModal && (
        <div className="r-modal-overlay" onClick={() => !einzelAnhangLoading && setEinzelAnhangModal(false)}>
          <div className="r-modal" onClick={e => e.stopPropagation()}>
            <div className="r-modal-header">
              <h3><Ico n="klammer" /> Anhang erstellen</h3>
              {!einzelAnhangLoading && <button className="r-modal-close" onClick={() => setEinzelAnhangModal(false)}><Ico n="schliessen" /></button>}
            </div>

            {einzelAnhangResult ? (
              <div className="r-modal-success">
                <div className="r-success-icon"><Ico n="haken" gr={40} /></div>
                <div><strong>
                  {einzelAnhangResult.action === "merged" ? "Angehänge zusammengeführt!" :
                   einzelAnhangResult.action === "extra" ? "Anhang als extra gespeichert!" :
                   "Anhang PDF gespeichert!"}
                </strong></div>
                <div className="r-modal-filename">{einzelAnhangResult.filename}</div>
                <div className="subtle">{einzelAnhangResult.total_images} Bild{einzelAnhangResult.total_images !== 1 ? "er" : ""}</div>
                {einzelAnhangResult.merged_with && (
                  <div className="subtle">Zusammengeführt mit: {einzelAnhangResult.merged_with}</div>
                )}
                <button className="r-btn primary" onClick={() => { setEinzelAnhangModal(false); setEinzelAnhangResult(null); }}>Schließen</button>
              </div>
            ) : einzelAnhangConflict ? (
              <>
                <div className="r-modal-info">
                  <div style={{ marginBottom: 8 }}>
                    <Ico n="warnung" /> Es gibt bereits einen Anhang für <strong>{einzelAnhangRE.trim().toUpperCase()}</strong>:
                  </div>
                  <div className="r-modal-filename">{einzelAnhangConflict.existing_file}</div>
                  <div style={{ marginTop: 12 }}>Was soll mit dem neuen Anhang passieren?</div>
                </div>
                {einzelAnhangError && <div className="r-error">{einzelAnhangError}</div>}
                <div className="r-modal-actions">
                  <button className="r-btn ghost" onClick={() => setEinzelAnhangModal(false)} disabled={einzelAnhangLoading}>Abbrechen</button>
                  <button className="r-btn ghost" onClick={() => createEinzelAnhang("extra")} disabled={einzelAnhangLoading}>
                    {einzelAnhangLoading ? "..." : <><Ico n="dokument" /> Beide behalten (extra)</>}
                  </button>
                  <button className="r-btn primary" onClick={() => createEinzelAnhang("merge")} disabled={einzelAnhangLoading}>
                    {einzelAnhangLoading ? "Zusammenführen..." : <><Ico n="verbinden" /> Zusammenführen</>}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="r-modal-info">
                  <div className="subtle">Karte: <strong>{einzelAnhangCard?.title}</strong></div>
                  <div className="subtle" style={{ marginTop: 4 }}>
                    {(einzelAnhangCard?.attachments || []).filter(a => (a.mimeType || "").startsWith("image/")).length} Bild
                    {(einzelAnhangCard?.attachments || []).filter(a => (a.mimeType || "").startsWith("image/")).length !== 1 ? "er" : ""} an dieser Karte
                  </div>
                </div>
                <div className="r-form-row">
                  <label>Rechnungsnummer <span className="r-hint-inline">(z.B. RE2026-0042)</span></label>
                  <input
                    autoFocus
                    value={einzelAnhangRE}
                    onChange={e => setEinzelAnhangRE(e.target.value)}
                    placeholder="z.B. RE3280"
                    onKeyDown={e => e.key === "Enter" && !einzelAnhangLoading && einzelAnhangRE.trim() && createEinzelAnhang()}
                  />
                </div>
                {einzelAnhangError && <div className="r-error">{einzelAnhangError}</div>}
                <div className="r-modal-actions">
                  <button className="r-btn ghost" onClick={() => setEinzelAnhangModal(false)} disabled={einzelAnhangLoading}>Abbrechen</button>
                  <button
                    className="r-btn primary"
                    onClick={() => createEinzelAnhang()}
                    disabled={einzelAnhangLoading || !einzelAnhangRE.trim()}
                  >
                    {einzelAnhangLoading ? "Prüfe..." : <><Ico n="klammer" /> Anhang erstellen</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ SCHNELL-ENTWURF MODAL ═══════════════ */}
      {schnellModal && (
        <div className="r-modal-overlay" onClick={() => !schnellLoading && setSchnellModal(null)}>
          <div className="r-modal" onClick={e => e.stopPropagation()}>
            <div className="r-modal-header">
              <h3>{schnellAlsAngebot ? <><Ico n="diagramm" /> Angebot erstellen</> : <><Ico n="dokument" /> Rechnung – Entwurf</>}</h3>
              {!schnellLoading && <button className="r-modal-close" onClick={() => setSchnellModal(null)}><Ico n="schliessen" /></button>}
            </div>

            {!schnellResult ? (
              <>
                {/* Typ-Toggle: Entwurf ↔ Angebot */}
                <div className="r-doc-type-toggle">
                  <button
                    className={`r-doc-type-btn${!schnellAlsAngebot ? " active" : ""}`}
                    onClick={() => setSchnellAlsAngebot(false)}
                  >
                    <Ico n="dokument" /> Entwurf
                  </button>
                  <button
                    className={`r-doc-type-btn angebot${schnellAlsAngebot ? " active" : ""}`}
                    onClick={() => setSchnellAlsAngebot(true)}
                  >
                    <Ico n="diagramm" /> Als Angebot
                  </button>
                </div>
                <div className="r-form-row">
                  <label>Prefix</label>
                  <div className="r-form-value"><strong>{schnellModal.card.parsed?.prefix || "–"}</strong></div>
                </div>

                <div className="r-form-row">
                  <label>Karte</label>
                  <div className="r-form-value subtle">{schnellModal.card.parsed?.beschreibung || schnellModal.card.title}</div>
                </div>

                <div className="r-form-row">
                  <label>Einleitungstext</label>
                  <div className="r-form-value subtle" style={{ fontStyle: "italic", fontSize: "0.85rem" }}>
                    Unsere Lieferungen/Leistungen stellen wir Ihnen wie folgt in Rechnung.
                  </div>
                </div>

                <div className="r-form-row">
                  <label>Arbeiten für <span className="r-hint-inline">(Freitext im Entwurf)</span></label>
                  <textarea
                    rows={2}
                    value={schnellFreitext}
                    onChange={e => setSchnellFreitext(e.target.value)}
                    placeholder="z.B. Objekt 005, Unter den Linden 5 – Dachrinne repariert"
                  />
                </div>

                <div className="r-form-row">
                  <label>Kunde {!schnellContactId && <span className="r-hint-inline warn"><Ico n="warnung" /> erforderlich</span>}</label>
                  <div className="r-contact-dropdown-wrap" style={{ position: "relative" }}>
                    <input
                      value={contactSearch}
                      onChange={e => {
                        setContactSearch(e.target.value);
                        setSchnellContactId("");
                        setContactDropdownOpen(true);
                      }}
                      onFocus={() => setContactDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setContactDropdownOpen(false), 150)}
                      placeholder="Kundennummer oder Name suchen…"
                      autoComplete="off"
                    />
                    {schnellContactId && (
                      <div className="r-hint" style={{ color: "var(--color-success, #4caf50)" }}>
                        <Ico n="haken" /> {schnellContactNummer && <><strong>{schnellContactNummer}</strong> – </>}{schnellContactName}
                      </div>
                    )}
                    {contactDropdownOpen && filteredContacts.length > 0 && (
                      <div className="r-contact-dropdown" style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                        background: "var(--color-surface, #1e1e2e)",
                        border: "1px solid var(--color-border, #333)",
                        borderRadius: 6, maxHeight: 220, overflowY: "auto",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.4)"
                      }}>
                        {filteredContacts.slice(0, 50).map(c => (
                          <div
                            key={c.id}
                            onMouseDown={() => selectContact(c)}
                            style={{
                              padding: "8px 12px", cursor: "pointer", fontSize: "0.875rem",
                              borderBottom: "1px solid var(--color-border, #333)",
                              background: c.id === schnellContactId ? "var(--color-primary-dim, #2a2a4a)" : "transparent"
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "var(--color-hover, #2a2a3a)"}
                            onMouseLeave={e => e.currentTarget.style.background = c.id === schnellContactId ? "var(--color-primary-dim, #2a2a4a)" : "transparent"}
                          >
                            {c.nummer != null && <strong style={{ marginRight: 8 }}>{c.nummer}</strong>}
                            {c.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Positionen aus Checkliste */}
                {schnellPosten.length > 0 && (
                  <div className="r-form-row">
                    <label>Rechnungspositionen <span className="r-hint-inline">({schnellPosten.length} aus Checkliste)</span></label>
                    <div className="r-posten-list">
                      {schnellPosten.map((p, i) => (
                        <div key={i} className="r-posten-row" style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                          <input
                            style={{ flex: 1, fontSize: "0.85rem" }}
                            value={p.name}
                            onChange={e => setSchnellPosten(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                          />
                          <input
                            type="number"
                            min={1}
                            style={{ width: 52, textAlign: "center", fontSize: "0.85rem" }}
                            value={p.menge}
                            onChange={e => setSchnellPosten(prev => prev.map((x, j) => j === i ? { ...x, menge: parseFloat(e.target.value) || 1 } : x))}
                          />
                          <span style={{ fontSize: "0.75rem", opacity: 0.5 }}>×</span>
                          <button
                            className="r-btn ghost"
                            style={{ padding: "2px 6px", fontSize: "0.75rem" }}
                            onClick={() => setSchnellPosten(prev => prev.filter((_, j) => j !== i))}
                          ><Ico n="schliessen" /></button>
                        </div>
                      ))}
                      <button
                        className="r-btn ghost"
                        style={{ fontSize: "0.8rem", marginTop: 4 }}
                        onClick={() => setSchnellPosten(prev => [...prev, { name: "", menge: 1, einheit: "Stück", preis_netto: 0, steuer: 19 }])}
                      >+ Position hinzufügen</button>
                    </div>
                  </div>
                )}
                {schnellPosten.length === 0 && (
                  <div className="r-form-row">
                    <label>Positionen</label>
                    <div className="r-form-value subtle" style={{ fontSize: "0.85rem" }}>
                      Keine Checkliste gefunden – 1 Standardposition wird erstellt.
                      <button
                        className="r-btn ghost"
                        style={{ fontSize: "0.8rem", marginLeft: 8 }}
                        onClick={() => setSchnellPosten([{ name: "", menge: 1, einheit: "Stück", preis_netto: 0, steuer: 19 }])}
                      >+ manuell hinzufügen</button>
                    </div>
                  </div>
                )}

                <div className="r-form-row">
                  <label>Bilder auf Trello-Karte</label>
                  <div className="r-form-value subtle">
                    <Ico n="bild" /> {schnellModal.card.attachments?.filter(a => a.mimeType?.startsWith("image/")).length || 0} Bild(er) werden exportiert
                  </div>
                </div>

                {schnellError && <div className="r-error">{schnellError}</div>}

                <div className="r-modal-actions">
                  <button className="r-btn ghost" onClick={() => setSchnellModal(null)} disabled={schnellLoading}>Abbrechen</button>
                  <button
                    className={`r-btn${schnellAlsAngebot ? " angebot-excel" : " primary"}`}
                    onClick={createSchnellEntwurf}
                    disabled={schnellLoading || !schnellContactId.trim()}
                  >
                    {schnellLoading
                      ? `Erstelle ${schnellAlsAngebot ? "Angebot" : "Entwurf"}...`
                      : schnellAlsAngebot ? <><Ico n="diagramm" /> Angebot erstellen</> : <><Ico n="dokument" /> Entwurf erstellen</>}
                  </button>
                </div>
              </>
            ) : (
              <div className="r-modal-success">
                <div className="r-success-icon"><Ico n="haken" gr={40} /></div>
                <div><strong>
                  {schnellResult.doc_type === "angebot" ? "Angebot erstellt" : "Entwurf erstellt"}: {schnellResult.rechnungsnummer}
                </strong></div>
                {schnellResult.pdf_filename && (
                  <div className="r-modal-filename">{schnellResult.pdf_filename}</div>
                )}
                {schnellResult.bilder_exportiert > 0 && (
                  <div className="subtle"><Ico n="bild" /> {schnellResult.bilder_exportiert} Bild(er) exportiert</div>
                )}
                <div className="subtle">Nummer + Link in Trello-Beschreibung eingetragen.</div>
                <a href={schnellResult.lexware_url} target="_blank" rel="noreferrer" className="r-btn primary" style={{ marginTop: 12 }}>
                  In Lexware öffnen →
                </a>
                <button className="r-btn ghost" style={{ marginTop: 8 }} onClick={() => { setSchnellModal(null); loadCards(); }}>Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ TAB: FORMULAR ═══════════════ */}
      {tab === "form" && formCard && (
        <div className="r-section">
          <div className="r-section-header">
            <button className="r-btn ghost" onClick={() => setTab("cards")}>← Zurück zu Cards</button>
            <h2>Rechnung erstellen</h2>
          </div>
          <div className="r-form">
            <div className="r-form-row">
              <label>Trello-Card</label>
              <div className="r-form-value subtle">{formCard.title}</div>
            </div>
            <div className="r-form-row">
              <label>Empfänger (Lexware-Kontakt)</label>
              <input
                className={!formContactId ? "error" : ""}
                value={formContactName}
                onChange={e => setFormContactName(e.target.value)}
                placeholder="Name (Anzeige)"
              />
              <input
                value={formContactId}
                onChange={e => setFormContactId(e.target.value)}
                placeholder="Lexware Kontakt-UUID"
                style={{ fontSize: "0.75rem", opacity: 0.7, marginTop: 4 }}
              />
              {!formContactId && <div className="r-hint"><Ico n="warnung" /> Keine UUID gefunden — bitte Lexware Kontakt-UUID direkt eingeben</div>}
            </div>
            <div className="r-form-row">
              <label>Rechnungsdatum</label>
              <input type="date" value={formDatum} onChange={e => setFormDatum(e.target.value)} />
            </div>
            <div className="r-form-row">
              <label>Einleitung</label>
              <input value={formIntro} onChange={e => setFormIntro(e.target.value)} />
            </div>

            <div className="r-posten-section">
              <div className="r-posten-header">
                <span>Rechnungsposten</span>
                <button className="r-btn small" onClick={addPosten}>+ Posten</button>
              </div>
              {formPosten.map((p, i) => (
                <div key={i} className="r-posten-row">
                  <input className="r-posten-name" value={p.name} onChange={e => updatePosten(i, "name", e.target.value)} placeholder="Bezeichnung *" />
                  <input className="r-posten-desc" value={p.description} onChange={e => updatePosten(i, "description", e.target.value)} placeholder="Beschreibung" />
                  <input className="r-posten-num w60" type="number" value={p.menge} min="0.01" step="0.01" onChange={e => updatePosten(i, "menge", parseFloat(e.target.value) || 1)} />
                  <input className="r-posten-str w70" value={p.einheit} onChange={e => updatePosten(i, "einheit", e.target.value)} placeholder="Einheit" />
                  <input className="r-posten-num w90" type="number" value={p.preis_netto} step="0.01" onChange={e => updatePosten(i, "preis_netto", parseFloat(e.target.value) || 0)} placeholder="Netto €" />
                  <select className="w70" value={p.steuer} onChange={e => updatePosten(i, "steuer", parseFloat(e.target.value))}>
                    <option value={19}>19%</option>
                    <option value={7}>7%</option>
                    <option value={0}>0%</option>
                  </select>
                  <button className="r-btn ghost small danger" onClick={() => removePosten(i)}><Ico n="schliessen" /></button>
                </div>
              ))}
              <div className="r-posten-sum">
                Gesamt netto: <strong>€ {formPosten.reduce((s, p) => s + (p.menge || 0) * (p.preis_netto || 0), 0).toFixed(2)}</strong>
              </div>
            </div>

            <div className="r-form-row">
              <label>Anhang / QR-Code URL <span className="r-hint-inline">(Dropbox-Link zu Foto/Dokument — wird als Hinweis in der Rechnung eingefügt)</span></label>
              {formCard.attachments?.length > 0 && (
                <div className="r-attachments">
                  {formCard.attachments.map(att => (
                    <button key={att.id} className={`r-att-btn ${formQrUrl === att.url ? "selected" : ""}`} onClick={() => setFormQrUrl(att.url)}>
                      <Ico n="klammer" /> {att.name}
                    </button>
                  ))}
                </div>
              )}
              <input value={formQrUrl} onChange={e => setFormQrUrl(e.target.value)} placeholder="https://... oder leer lassen" />
            </div>

            {formError && <div className="r-error">{formError}</div>}
            {formResult && (
              <div className="r-success">
                <Ico n="haken" /> Rechnung als Entwurf erstellt!
                <a href={formResult.lexware_url} target="_blank" rel="noreferrer" className="r-btn primary" style={{ marginLeft: 12 }}>
                  In Lexware öffnen →
                </a>
              </div>
            )}

            <div className="r-form-actions">
              <button className="r-btn primary large" onClick={createRechnung} disabled={formSaving || !formContactId}>
                {formSaving ? "Erstelle..." : <><Ico n="beleg" /> Als Entwurf in Lexware speichern</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ TAB: ARCHIV ═══════════════ */}
      {tab === "archiv" && (
        <div className="r-section">
          <div className="r-section-header">
            <h2><Ico n="ordner" /> Anhang-Archiv <span className="r-label-hint">Dateien nach RE-Nummer</span></h2>
            <button className="r-btn primary" onClick={loadArchiv} disabled={archivLoading}>
              {archivLoading ? "Lädt..." : <><Ico n="aktualisieren" /> Aktualisieren</>}
            </button>
          </div>
          {archivFolder && archivAvailable && <div className="r-archiv-path subtle">{archivFolder}</div>}
          {!archivAvailable && !archivLoading && (
            <div className="r-warning">
              <Ico n="ordner" /> Anhang-Archiv ist nur lokal verfügbar (Dropbox-Zugriff erforderlich).
            </div>
          )}
          {archivError && <div className="r-error">{archivError}</div>}
          {archivPdfResult && (
            <div className="r-success">
              <Ico n="haken" /> PDF gespeichert: <strong>{archivPdfResult.filename}</strong> ({archivPdfResult.total_images} Bilder)
            </div>
          )}

          {/* ── Merge-Box ── */}
          <div className="r-merge-box">
            <div className="r-merge-header">
              <div>
                <strong><Ico n="verbinden" /> Anhang zusammenführen</strong>
                <span className="r-hint-inline"> — Rechnungen aus Batch Output + passende Anhang-PDFs</span>
              </div>
              {mergePreview !== null && (
                <span className={`r-merge-badge ${mergePreview.count > 0 ? "active" : ""}`}>
                  {mergePreview.count} Paar{mergePreview.count !== 1 ? "e" : ""} gefunden
                </span>
              )}
              <button
                className="r-btn primary"
                onClick={runMerge}
                disabled={mergeLoading || (mergePreview?.count === 0)}
              >
                {mergeLoading ? "Führe zusammen..." : <><Ico n="verbinden" /> Jetzt zusammenführen</>}
              </button>
            </div>
            {mergePreview?.pairs?.length > 0 && (
              <div className="r-merge-pairs">
                {mergePreview.pairs.map((p, i) => (
                  <div key={i} className="r-merge-pair">
                    <span className="r-merge-re">{p.re_nr}</span>
                    <span className="r-merge-rechnung"><Ico n="dokument" /> {p.rechnung_file}</span>
                    <span className="r-merge-plus">+</span>
                    <span className="r-merge-anhang"><Ico n="klammer" /> {p.anhang_file}</span>
                  </div>
                ))}
              </div>
            )}
            {mergeResult && (
              <div className="r-success" style={{ marginTop: 8 }}>
                <Ico n="haken" /> {mergeResult.merged?.length} Datei{mergeResult.merged?.length !== 1 ? "en" : ""} zusammengeführt
                {mergeResult.merged?.map((m, i) => (
                  <div key={i} className="subtle small" style={{ marginTop: 4 }}>→ {m.output}</div>
                ))}
                {mergeResult.errors?.length > 0 && (
                  <div className="r-error" style={{ marginTop: 8 }}>
                    {mergeResult.errors.map((e, i) => <div key={i}><Ico n="schliessen" /> {e.rechnung}: {e.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          {!archivLoading && archivGroups.length === 0 && (
            <div className="r-empty">Keine Dateien mit RE-Nummer im Ordner gefunden.</div>
          )}
          <div className="r-archiv-list">
            {archivGroups.map(group => (
              <div key={group.re_nr} className="r-archiv-group">
                <div className="r-archiv-header">
                  <div className="r-archiv-re">{group.re_nr}</div>
                  <div className="r-archiv-meta">
                    {group.image_count > 0 && <span className="r-tag attach"><Ico n="bild" /> {group.image_count} Bild{group.image_count !== 1 ? "er" : ""}</span>}
                    {group.pdf_count > 0 && <span className="r-tag ok"><Ico n="dokument" /> {group.pdf_count} PDF</span>}
                  </div>
                  {group.image_count > 0 && (
                    <button
                      className="r-btn primary small"
                      onClick={() => buildArchivPdf(group.re_nr)}
                      disabled={archivPdfLoading === group.re_nr}
                    >
                      {archivPdfLoading === group.re_nr ? "Erstelle..." : <><Ico n="klammer" /> In PDF packen</>}
                    </button>
                  )}
                </div>
                <div className="r-archiv-files">
                  {group.files.map(f => (
                    <div key={f.name} className={`r-archiv-file ${f.is_pdf ? "is-pdf" : ""}`}>
                      <span className="r-archiv-file-icon"><Ico n={f.is_pdf ? "dokument" : "bild"} /></span>
                      <span className="r-archiv-file-name">{f.name}</span>
                      <span className="r-archiv-file-size subtle">{(f.size / 1024).toFixed(0)} KB</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════ ANGEBOT AUS EXCEL MODAL ═══════════════ */}
      {angebotModal && (
        <div className="r-modal-overlay" onClick={() => !angebotLoading && !angebotPreviewLoading && setAngebotModal(null)}>
          <div className="r-modal r-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="r-modal-header">
              <h3><Ico n="diagramm" /> Angebot aus Excel erstellen</h3>
              {!angebotLoading && !angebotPreviewLoading && (
                <button className="r-modal-close" onClick={() => setAngebotModal(null)}><Ico n="schliessen" /></button>
              )}
            </div>

            {!angebotResult ? (
              <>
                <div className="r-form-row">
                  <label>Trello-Karte</label>
                  <div className="r-form-value subtle">{angebotModal.card.parsed?.beschreibung || angebotModal.card.title}</div>
                </div>

                {/* Excel-Anhang auswählen */}
                <div className="r-form-row">
                  <label>Excel-Anhang (xlsx)</label>
                  {angebotAttachments.length === 0 ? (
                    <div className="r-hint warn"><Ico n="warnung" /> Keine .xlsx-Dateien an dieser Karte. Bitte Excel-Datei an die Trello-Karte anhängen.</div>
                  ) : (
                    <select value={angebotAttId} onChange={e => {
                      const id = e.target.value;
                      setAngebotAttId(id);
                      setAngebotPreview(null);
                      setAngebotSheets([]);
                      setAngebotSheet("");
                      setAngebotSchema("v1");
                      setAngebotLosGroups({});
                      if (id) loadAngebotSheets(angebotModal.card.card_id, id);
                    }}>
                      <option value="">– Datei wählen –</option>
                      {angebotAttachments.map(a => (
                        <option key={a.id} value={a.id}>{a.name} ({(a.bytes / 1024).toFixed(0)} KB)</option>
                      ))}
                    </select>
                  )}
                  {angebotAttId && angebotSchema === "v2" && (
                    <span style={{ marginLeft: 8, fontSize: "0.75rem", background: "var(--color-primary, #7c6af7)", color: "#fff", borderRadius: 4, padding: "2px 6px" }}><Ico n="klemmbrett" /> Schema 2</span>
                  )}
                </div>

                {/* Fremdes Format: KI-Prompt / Selbst mappen */}
                {angebotAttId && (
                  <div style={{
                    marginBottom: 12, padding: "10px 12px", borderRadius: 6,
                    background: angebotSchema === "v2" ? "var(--color-surface-2, #252535)" : "rgba(255, 180, 0, 0.08)",
                    border: `1px solid ${angebotSchema === "v2" ? "var(--color-border, #333)" : "rgba(255, 180, 0, 0.45)"}`,
                  }}>
                    {angebotSchema !== "v2" && (
                      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "0.86rem" }}>
                        Format nicht als Schema 2 erkannt — Excel erst ins Zielformat bringen:
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="r-btn ghost"
                        style={{ fontSize: "0.82rem" }}
                        onClick={() => setFremdModal({ source: { card_id: angebotModal.card.card_id, attachment_id: angebotAttId }, sheet: angebotSheet, mode: "mapping" })}
                      >Selbst mappen</button>
                      <button
                        className="r-btn ghost"
                        style={{ fontSize: "0.82rem" }}
                        onClick={() => setFremdModal({ source: { card_id: angebotModal.card.card_id, attachment_id: angebotAttId }, sheet: angebotSheet, mode: "prompt" })}
                      >KI-Prompt kopieren</button>
                    </div>
                  </div>
                )}

                {/* Sheet-Auswahl (wenn mehrere vorhanden) */}
                {angebotSheets.length > 1 && (
                  <div className="r-form-row">
                    <label>Sheet</label>
                    <select value={angebotSheet} onChange={e => { setAngebotSheet(e.target.value); setAngebotPreview(null); }}>
                      {angebotSheets.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                {/* Datum */}
                <div className="r-form-row">
                  <label>Angebotsdatum</label>
                  <input
                    type="date"
                    value={angebotDatum}
                    onChange={e => { setAngebotDatum(e.target.value); setAngebotPreview(null); }}
                  />
                </div>

                {/* Kontakt */}
                <div className="r-form-row">
                  <label>Lexware-Kontakt {!angebotContactId && <span className="r-hint-inline warn"><Ico n="warnung" /> erforderlich</span>}</label>
                  <div style={{ position: "relative" }}>
                    <input
                      value={angebotContactSearch}
                      onChange={e => { setAngebotContactSearch(e.target.value); setAngebotContactId(""); setAngebotContactDropdown(true); }}
                      onFocus={() => { setAngebotContactDropdown(true); loadAllContacts(); }}
                      onBlur={() => setTimeout(() => setAngebotContactDropdown(false), 150)}
                      placeholder="Kundennummer oder Name suchen…"
                      autoComplete="off"
                    />
                    {angebotContactId && (
                      <div className="r-hint" style={{ color: "var(--color-success, #4caf50)" }}>
                        <Ico n="haken" /> {angebotContactSearch}
                      </div>
                    )}
                    {angebotContactDropdown && angebotFilteredContacts.length > 0 && (
                      <div className="r-contact-dropdown" style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                        background: "var(--color-surface, #1e1e2e)",
                        border: "1px solid var(--color-border, #333)",
                        borderRadius: 6, maxHeight: 220, overflowY: "auto",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.4)"
                      }}>
                        {angebotFilteredContacts.slice(0, 30).map(c => (
                          <div
                            key={c.id}
                            className="r-contact-option"
                            style={{ padding: "8px 12px", cursor: "pointer" }}
                            onMouseDown={() => {
                              setAngebotContactId(c.id);
                              setAngebotContactSearch(`${c.nummer ? c.nummer + " – " : ""}${c.name}`);
                              setAngebotContactDropdown(false);
                            }}
                          >
                            {c.nummer && <strong style={{ marginRight: 8 }}>{c.nummer}</strong>}
                            {c.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Vorschau */}
                {angebotAttId && angebotContactId && !angebotPreview && (
                  <div className="r-form-row">
                    <button
                      className="r-btn ghost"
                      onClick={loadAngebotPreview}
                      disabled={angebotPreviewLoading}
                    >
                      {angebotPreviewLoading ? "Lade Vorschau…" : <><Ico n="suche" /> Vorschau anzeigen</>}
                    </button>
                  </div>
                )}

                {angebotPreview && (
                  <div className="r-angebot-preview">
                    <div className="r-angebot-preview-header">
                      <strong>{angebotPreview.parsed?.titel || angebotPreview.parsed?.objekt || "Angebot"}</strong>
                      {angebotPreview.parsed?.angebot_nr && <span className="subtle"> · {angebotPreview.parsed.angebot_nr}</span>}
                      {angebotSchema === "v2" && <span style={{ marginLeft: 6, fontSize: "0.72rem", background: "var(--color-primary, #7c6af7)", color: "#fff", borderRadius: 4, padding: "1px 5px" }}>Schema 2</span>}
                      <span className="r-preview-stats">
                        {angebotPreview.positionen_count} Pos. · {angebotPreview.line_items_count} Zeilen
                      </span>
                    </div>
                    <div className="r-angebot-item-list">
                      {(angebotPreview.parsed?.items || []).slice(0, 25).map((item, i) => (
                        <div key={i} className={`r-angebot-item r-angebot-item-${item.type}`}>
                          {(item.type === "section" || item.type === "freitext") && (
                            <div className="r-angebot-section">{item.title || item.text}</div>
                          )}
                          {item.type === "los" && (
                            <div style={{ fontWeight: 700, color: "var(--color-primary, #7c6af7)", padding: "4px 0" }}>
                              ── {item.label}. {item.title} ──
                            </div>
                          )}
                          {(item.type === "item" || item.type === "artikel") && (
                            <div>
                              <div className="r-angebot-pos">
                                <span className="r-angebot-pos-nr">{item.pos}</span>
                                <span className="r-angebot-pos-desc">{item.desc?.slice(0, 80)}{item.desc?.length > 80 ? "…" : ""}</span>
                                {(item.price ?? item.einzelpreis) != null && (
                                  <span className="r-angebot-pos-price">
                                    {(item.price ?? item.einzelpreis).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                                  </span>
                                )}
                              </div>
                              {item.beschreibungstext && (
                                <div style={{ fontSize: "0.78rem", color: "var(--color-text-dim, #888)", paddingTop: 1, paddingLeft: 44 }}>{item.beschreibungstext.slice(0, 120)}</div>
                              )}
                            </div>
                          )}
                          {item.type === "zwischensumme" && (
                            <div style={{ fontStyle: "italic", color: "var(--color-text-dim, #888)", padding: "2px 0", textAlign: "right" }}>
                              {`── Zwischensumme - ${item.label || ""}${item.los_title ? `: ${item.los_title}` : ""}: ${item.gesamtpreis != null ? item.gesamtpreis.toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €" : "?"} ──`}
                            </div>
                          )}
                          {item.type === "text" && (
                            <div className="r-angebot-note subtle">{item.text?.slice(0, 60)}…</div>
                          )}
                        </div>
                      ))}
                      {(angebotPreview.parsed?.items || []).length > 25 && (
                        <div className="subtle" style={{ padding: "6px 0" }}>
                          … +{angebotPreview.parsed.items.length - 25} weitere Zeilen
                        </div>
                      )}

                      {/* Abschlussblöcke Preview */}
                      {angebotZusammenfassung && (() => {
                        const items = angebotPreview.parsed?.items || [];
                        const zwi = items.filter(i => i.type === "zwischensumme" && i.label);
                        const entries = zwi.length > 0
                          ? zwi.map(i => ({ title: i.los_title || i.label, amt: i.gesamtpreis }))
                          : items.filter(i => i.type === "artikel" && i.gesamtpreis).map(i => ({ title: i.desc?.slice(0, 40), amt: i.gesamtpreis }));
                        if (!entries.length) return null;
                        const netto = items.filter(i => i.type === "artikel").reduce((s, i) => s + (i.gesamtpreis || 0), 0);
                        const mwstB = Math.round(netto * 19) / 100;
                        return (
                          <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4, fontSize: "0.8rem", fontFamily: "monospace" }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Zusammenfassung</div>
                            {entries.map((e, i) => (
                              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                <span>• {e.title}</span>
                                <span>{e.amt?.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                              </div>
                            ))}
                            <div style={{ borderTop: "1px solid var(--color-border, #444)", marginTop: 4, paddingTop: 4 }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}><span>Nettobetrag</span><span>{netto.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span></div>
                              <div style={{ display: "flex", justifyContent: "space-between" }}><span>zzgl. MwSt 19%</span><span>{mwstB.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span></div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}><span>Angebotspreis</span><span>{(netto + mwstB).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span></div>
                            </div>
                          </div>
                        );
                      })()}
                      {angebotZahlungen && angebotZahlungsTexte.length > 0 && (
                        <div style={{ marginTop: 6, padding: "8px 10px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4, fontSize: "0.8rem" }}>
                          <div style={{ fontWeight: 600, marginBottom: 3 }}>Zahlungsmodalitäten</div>
                          {angebotZahlungsTexte.map((t, i) => <div key={i}>{t}</div>)}
                        </div>
                      )}
                      {angebotAusfuehrung && angebotAusfuehrungText && (
                        <div style={{ marginTop: 6, padding: "8px 10px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4, fontSize: "0.8rem" }}>
                          <div style={{ fontWeight: 600, marginBottom: 3 }}>Ausführungszeit</div>
                          <div>{angebotAusfuehrungText}</div>
                        </div>
                      )}
                    </div>
                    {/* Standardtexte-Dropdown */}
                    {(textVorlagen.length > 0 || textVorlagenLoading) && (
                      <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--color-surface-2, #252535)", borderRadius: 6 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "0.88rem" }}><Ico n="stift" /> Standardtext einfügen</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <select
                            value={selectedTextVorlage}
                            onChange={e => setSelectedTextVorlage(e.target.value)}
                            style={{ flex: "1 1 180px", padding: "3px 6px", fontSize: "0.82rem", background: "var(--color-surface, #1a1a2e)", color: "inherit", border: "1px solid var(--color-border, #333)", borderRadius: 4 }}
                          >
                            <option value="">— Vorlage wählen —</option>
                            {textVorlagen.map(tv => (
                              <option key={tv.id} value={tv.id}>{tv.name}</option>
                            ))}
                          </select>
                          <select
                            value={insertPosition}
                            onChange={e => setInsertPosition(e.target.value)}
                            style={{ padding: "3px 6px", fontSize: "0.82rem", background: "var(--color-surface, #1a1a2e)", color: "inherit", border: "1px solid var(--color-border, #333)", borderRadius: 4 }}
                          >
                            <option value="intro">Am Anfang</option>
                            <option value="end">Am Ende</option>
                          </select>
                          <button
                            className="r-btn ghost"
                            style={{ padding: "3px 10px", fontSize: "0.82rem" }}
                            onClick={() => {
                              const tv = textVorlagen.find(t => t.id === selectedTextVorlage);
                              setInsertedTexts(prev => [...prev, { id: Date.now().toString(), text: tv.content || tv.name, insertAfter: insertPosition }]);
                              setSelectedTextVorlage("");
                            }}
                          >+ Einfügen</button>
                        </div>
                        {insertedTexts.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            {insertedTexts.map(it => (
                              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontSize: "0.8rem", padding: "3px 6px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4 }}>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  [{it.insertAfter === "intro" ? "Anfang" : "Ende"}] {it.text.slice(0, 80)}
                                </span>
                                <button
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-dim, #888)", padding: "0 2px" }}
                                  onClick={() => setInsertedTexts(prev => prev.filter(x => x.id !== it.id))}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Zuordnung für ungegruppierte Artikel (Schema 2) */}
                    {angebotSchema === "v2" && (angebotPreview.parsed?.ungrouped_artikel || []).length > 0 && (
                      <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--color-surface-2, #252535)", borderRadius: 6 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: "0.88rem" }}>Ungegruppierte Artikel – Gruppe zuordnen</div>
                        {(() => {
                          const excelLosCount = angebotPreview.excel_los_count || 0;
                          // excelGroups: Labels der Excel-LOS (A, B, ...) aus parsed items
                          const excelGroups = (angebotPreview.parsed?.items || [])
                            .filter(i => i.type === "los")
                            .map(i => i.label);
                          const maxNum = Math.max(0, ...Object.values(angebotLosGroups).filter(v => typeof v === "number" && v > 0));
                          // Summen: excelLabel oder neue Nummer
                          const losSums = {};
                          const losExcelSums = {};
                          (angebotPreview.parsed?.ungrouped_artikel || []).forEach(g => {
                            const val = angebotLosGroups[g.group_key];
                            const sum = (g.artikel || []).reduce((acc, a) => acc + (a.gesamtpreis || 0), 0);
                            if (typeof val === "number" && val > 0) losSums[val] = (losSums[val] || 0) + sum;
                            else if (typeof val === "string") losExcelSums[val] = (losExcelSums[val] || 0) + sum;
                          });
                          const getNewLabel = (num) => String.fromCharCode(65 + excelLosCount + num - 1);
                          return (
                            <>
                              {angebotPreview.parsed.ungrouped_artikel.map(g => {
                                const curVal = angebotLosGroups[g.group_key] ?? null;
                                return (
                                  <div key={g.group_key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: "0.85rem" }}>
                                    <div style={{ flex: 1 }}>
                                      <span style={{ fontWeight: 500 }}>{g.group_title || `Gruppe ${g.group_key}`}</span>
                                      <span className="subtle" style={{ marginLeft: 6 }}>({g.artikel.length} Artikel)</span>
                                    </div>
                                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                      <button
                                        className={`r-btn ${!curVal ? "primary" : "ghost"}`}
                                        style={{ padding: "2px 8px", fontSize: "0.78rem" }}
                                        onClick={() => setAngebotLosGroups(prev => ({ ...prev, [g.group_key]: null }))}
                                      >–</button>
                                      {/* Excel-LOS Gruppen als Ziel */}
                                      {excelGroups.map(lbl => (
                                        <button
                                          key={`ex_${lbl}`}
                                          className={`r-btn ${curVal === `ex_${lbl}` ? "primary" : "ghost"}`}
                                          style={{ padding: "2px 8px", fontSize: "0.78rem", fontWeight: curVal === `ex_${lbl}` ? 700 : 400 }}
                                          onClick={() => setAngebotLosGroups(prev => ({ ...prev, [g.group_key]: curVal === `ex_${lbl}` ? null : `ex_${lbl}` }))}
                                          title={`Zu Gruppe ${lbl} hinzufügen`}
                                        >{lbl}</button>
                                      ))}
                                      {/* Neue Gruppen (B, C... nach Excel-LOS) */}
                                      {Array.from({ length: maxNum + 1 }, (_, i) => i + 1).map(n => (
                                        <button
                                          key={n}
                                          className={`r-btn ${curVal === n ? "primary" : "ghost"}`}
                                          style={{ padding: "2px 8px", fontSize: "0.78rem", fontWeight: curVal === n ? 700 : 400 }}
                                          onClick={() => setAngebotLosGroups(prev => ({ ...prev, [g.group_key]: curVal === n ? null : n }))}
                                        >{getNewLabel(n)}</button>
                                      ))}
                                      <button
                                        className="r-btn ghost"
                                        style={{ padding: "2px 8px", fontSize: "0.78rem" }}
                                        onClick={() => setAngebotLosGroups(prev => ({ ...prev, [g.group_key]: maxNum + 1 }))}
                                        title="Neue Gruppe erstellen"
                                      >+ {getNewLabel(maxNum + 1)}</button>
                                    </div>
                                  </div>
                                );
                              })}
                              {(Object.keys(losSums).length > 0 || Object.keys(losExcelSums).length > 0) && (
                                <div style={{ marginTop: 10, borderTop: "1px solid var(--color-border, #333)", paddingTop: 8, fontSize: "0.82rem" }}>
                                  {Object.keys(losExcelSums).sort().map(lbl => (
                                    <div key={lbl} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                      <span>Zwischensumme {lbl.replace("ex_", "")}</span>
                                      <span style={{ fontWeight: 600 }}>{losExcelSums[lbl].toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                                    </div>
                                  ))}
                                  {Object.keys(losSums).sort((a,b) => a-b).map(n => (
                                    <div key={n} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                      <span>Zwischensumme {getNewLabel(Number(n))}</span>
                                      <span style={{ fontWeight: 600 }}>{losSums[n].toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Neues Angebot Button nur */}

                {/* ── Abschlussblöcke ───────────────────────────── */}
                {angebotSchema === "v2" && (
                  <div style={{ marginTop: 14, borderTop: "1px solid var(--color-border, #333)", paddingTop: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: "0.9rem" }}>Abschlussblöcke</div>

                    {/* Zusammenfassung */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: "0.88rem" }}>
                      <input type="checkbox" checked={angebotZusammenfassung} onChange={e => setAngebotZusammenfassung(e.target.checked)} />
                      Zusammenfassung (Zwischensummen + Netto/MwSt/Gesamt)
                    </label>

                    {/* Zahlungsmodalitäten */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer", fontSize: "0.88rem" }}>
                      <input type="checkbox" checked={angebotZahlungen} onChange={e => setAngebotZahlungen(e.target.checked)} />
                      Zahlungsmodalitäten
                    </label>
                    {angebotZahlungen && (
                      <div style={{ marginLeft: 24, marginBottom: 8 }}>
                        {angebotZahlungsTexte.map((t, i) => (
                          <div key={i} style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                            <input
                              value={t}
                              onChange={e => setAngebotZahlungsTexte(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                              style={{ flex: 1, fontSize: "0.82rem", padding: "3px 6px", background: "var(--color-bg2, #1a1a1a)", color: "var(--color-text, #eee)", border: "1px solid var(--color-border, #444)", borderRadius: 4 }}
                            />
                            <button className="r-btn ghost" style={{ padding: "2px 7px", fontSize: "0.78rem" }} onClick={() => setAngebotZahlungsTexte(prev => prev.filter((_, j) => j !== i))}><Ico n="schliessen" /></button>
                          </div>
                        ))}
                        <button className="r-btn ghost" style={{ fontSize: "0.8rem", padding: "3px 10px" }} onClick={() => setAngebotZahlungsTexte(prev => [...prev, ""])}>+ Zeile</button>
                      </div>
                    )}

                    {/* Ausführungszeit */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer", fontSize: "0.88rem" }}>
                      <input type="checkbox" checked={angebotAusfuehrung} onChange={e => setAngebotAusfuehrung(e.target.checked)} />
                      Ausführungszeit
                    </label>
                    {angebotAusfuehrung && (
                      <input
                        value={angebotAusfuehrungText}
                        onChange={e => setAngebotAusfuehrungText(e.target.value)}
                        placeholder="Ca. drei Wochen nach Auftragserteilung"
                        style={{ marginLeft: 24, width: "calc(100% - 24px)", fontSize: "0.82rem", padding: "4px 8px", background: "var(--color-bg2, #1a1a1a)", color: "var(--color-text, #eee)", border: "1px solid var(--color-border, #444)", borderRadius: 4, marginBottom: 4 }}
                      />
                    )}
                  </div>
                )}

                {angebotError && <div className="r-error">{angebotError}</div>}

                <div className="r-modal-actions" style={{ marginTop: 14 }}>
                  <button className="r-btn ghost" onClick={() => setAngebotModal(null)} disabled={angebotLoading}>Abbrechen</button>
                  <button
                    className="r-btn primary"
                    onClick={createAngebot}
                    disabled={angebotLoading || !angebotAttId || !angebotContactId}
                  >
                    {angebotLoading ? "Erstelle Angebot…" : <><Ico n="diagramm" /> Angebot in Lexware erstellen</>}
                  </button>
                </div>
              </>
            ) : (
              <div className="r-modal-success">
                <div className="r-success-icon"><Ico n="haken" gr={40} /></div>
                <div><strong>Angebot erstellt!</strong></div>
                {angebotResult.angebot_nr && <div className="subtle">{angebotResult.angebot_nr}</div>}
                {angebotResult.titel && <div>{angebotResult.titel}</div>}
                <div className="subtle">{angebotResult.positionen_count} Positionen · {angebotResult.datum}</div>
                {angebotResult.old_quotation_id && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "#fff3cd", borderRadius: 6, fontSize: 13, color: "#856404" }}>
                    <Ico n="warnung" /> Neues Angebot erstellt (Lexware API unterstützt kein Überschreiben).
                    {angebotResult.old_voucher_number && <> Bitte <strong>{angebotResult.old_voucher_number}</strong> manuell in Lexware löschen.</>}
                    {angebotResult.old_lexware_url && (
                      <> <a href={angebotResult.old_lexware_url} target="_blank" rel="noreferrer" style={{ color: "#856404" }}>→ Altes Angebot öffnen</a></>
                    )}
                  </div>
                )}
                {angebotResult.lexware_url && (
                  <a href={angebotResult.lexware_url} target="_blank" rel="noreferrer" className="r-btn primary" style={{ marginTop: 12 }}>
                    → Neues Angebot in Lexware öffnen
                  </a>
                )}
                <button className="r-btn ghost" onClick={() => setAngebotModal(null)} style={{ marginTop: 8 }}>Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ UPLOAD-ANGEBOT MODAL ═══════════════ */}
      {uploadModal && (
        <div className="r-modal-overlay" onClick={() => !uploadLoading && !uploadPreviewLoading && setUploadModal(false)}>
          <div className="r-modal r-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="r-modal-header">
              <h3><Ico n="diagramm" /> Angebot aus Excel erstellen</h3>
              {!uploadLoading && !uploadPreviewLoading && (
                <button className="r-modal-close" onClick={() => setUploadModal(false)}><Ico n="schliessen" /></button>
              )}
            </div>

            {!uploadResult ? (
              <>
                {/* Drag & Drop Zone */}
                <div
                  className={`r-upload-zone${uploadIsDragging ? " dragging" : ""}${uploadFile ? " has-file" : ""}`}
                  onDragOver={e => { e.preventDefault(); setUploadIsDragging(true); }}
                  onDragLeave={() => setUploadIsDragging(false)}
                  onDrop={e => {
                    e.preventDefault();
                    setUploadIsDragging(false);
                    const f = e.dataTransfer.files[0];
                    if (f && f.name.toLowerCase().endsWith(".xlsx")) {
                      handleUploadFile(f);
                    } else {
                      setUploadError("Nur .xlsx Dateien erlaubt");
                    }
                  }}
                  onClick={() => document.getElementById("upload-xlsx-input").click()}
                  style={{
                    border: `2px dashed ${uploadIsDragging ? "var(--color-primary, #7c6af7)" : uploadFile ? "var(--color-success, #4caf50)" : "var(--color-border, #444)"}`,
                    borderRadius: 8, padding: "24px 16px", textAlign: "center",
                    cursor: "pointer", marginBottom: 16, transition: "border-color 0.15s",
                    background: uploadIsDragging ? "var(--color-primary-dim, #2a2a4a)" : "transparent",
                  }}
                >
                  <input
                    id="upload-xlsx-input"
                    type="file"
                    accept=".xlsx"
                    style={{ display: "none" }}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) handleUploadFile(f);
                    }}
                  />
                  {uploadFile ? (
                    <div>
                      <div><Ico n="diagramm" gr={28} /></div>
                      <div style={{ fontWeight: 600, marginTop: 4 }}>{uploadFile.name}</div>
                      <div className="subtle" style={{ fontSize: "0.8rem" }}>
                        ({(uploadFile.size / 1024).toFixed(0)} KB) – klicken zum Ändern
                        {uploadSheetLoading && <span> lädt …</span>}
                        {!uploadSheetLoading && uploadSchema === "v2" && <span style={{ marginLeft: 8, background: "var(--color-primary, #7c6af7)", color: "#fff", borderRadius: 4, padding: "1px 5px" }}><Ico n="klemmbrett" /> Schema 2</span>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div><Ico n="ordner" gr={28} /></div>
                      <div style={{ marginTop: 4 }}>Excel-Datei hier ablegen oder klicken</div>
                      <div className="subtle" style={{ fontSize: "0.8rem" }}>Nur .xlsx</div>
                    </div>
                  )}
                </div>

                {/* Sheet-Auswahl-Popup */}
                {uploadSheetModal && uploadSheets.length > 1 && (
                  <div style={{ marginBottom: 12, padding: "10px 12px", background: "var(--color-surface-2, #252535)", borderRadius: 6, border: "1px solid var(--color-border, #333)" }}>
                    <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "0.88rem" }}><Ico n="klemmbrett" /> Welches Sheet soll verwendet werden?</div>
                    <select value={uploadSheet} onChange={e => setUploadSheet(e.target.value)} style={{ width: "100%", marginBottom: 6 }}>
                      {uploadSheets.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button className="r-btn primary" style={{ fontSize: "0.82rem" }} onClick={() => setUploadSheetModal(false)}><Ico n="haken" /> Sheet übernehmen</button>
                  </div>
                )}

                {/* Sheet-Anzeige (wenn bereits gewählt, mehrere vorhanden) */}
                {!uploadSheetModal && uploadSheets.length > 1 && uploadSheet && (
                  <div className="r-form-row">
                    <label>Sheet</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{uploadSheet}</span>
                      <button className="r-btn ghost" style={{ fontSize: "0.78rem", padding: "2px 8px" }} onClick={() => setUploadSheetModal(true)}>ändern</button>
                    </div>
                  </div>
                )}

                {/* Fremdes Format: KI-Prompt / Selbst mappen */}
                {uploadFile && !uploadSheetModal && !uploadSheetLoading && (
                  <div style={{
                    marginBottom: 12, padding: "10px 12px", borderRadius: 6,
                    background: uploadSchema === "v2" ? "var(--color-surface-2, #252535)" : "rgba(255, 180, 0, 0.08)",
                    border: `1px solid ${uploadSchema === "v2" ? "var(--color-border, #333)" : "rgba(255, 180, 0, 0.45)"}`,
                  }}>
                    {uploadSchema !== "v2" && (
                      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "0.86rem" }}>
                        Format nicht als Schema 2 erkannt — Excel erst ins Zielformat bringen:
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="r-btn ghost"
                        style={{ fontSize: "0.82rem" }}
                        onClick={() => setFremdModal({ source: { file: uploadFile }, sheet: uploadSheet, mode: "mapping" })}
                      >Selbst mappen</button>
                      <button
                        className="r-btn ghost"
                        style={{ fontSize: "0.82rem" }}
                        onClick={() => setFremdModal({ source: { file: uploadFile }, sheet: uploadSheet, mode: "prompt" })}
                      >KI-Prompt kopieren</button>
                    </div>
                  </div>
                )}

                {/* Datum */}
                <div className="r-form-row">
                  <label>Angebotsdatum</label>
                  <input
                    type="date"
                    value={uploadDatum}
                    onChange={e => { setUploadDatum(e.target.value); setUploadPreview(null); }}
                  />
                </div>

                {/* Kontakt */}
                <div className="r-form-row">
                  <label>Lexware-Kontakt {!uploadContactId && <span className="r-hint-inline warn"><Ico n="warnung" /> erforderlich</span>}</label>
                  <div style={{ position: "relative" }}>
                    <input
                      value={uploadContactSearch}
                      onChange={e => { setUploadContactSearch(e.target.value); setUploadContactId(""); setUploadContactDropdown(true); }}
                      onFocus={() => { setUploadContactDropdown(true); loadAllContacts(); }}
                      onBlur={() => setTimeout(() => setUploadContactDropdown(false), 150)}
                      placeholder="Kundennummer oder Name suchen…"
                      autoComplete="off"
                    />
                    {uploadContactId && (
                      <div className="r-hint" style={{ color: "var(--color-success, #4caf50)" }}>
                        <Ico n="haken" /> {uploadContactSearch}
                      </div>
                    )}
                    {uploadContactDropdown && uploadFilteredContacts.length > 0 && (
                      <div className="r-contact-dropdown" style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                        background: "var(--color-surface, #1e1e2e)",
                        border: "1px solid var(--color-border, #333)",
                        borderRadius: 6, maxHeight: 220, overflowY: "auto",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.4)"
                      }}>
                        {uploadFilteredContacts.slice(0, 30).map(c => (
                          <div
                            key={c.id}
                            className="r-contact-option"
                            style={{ padding: "8px 12px", cursor: "pointer" }}
                            onMouseDown={() => {
                              setUploadContactId(c.id);
                              setUploadContactSearch(`${c.nummer ? c.nummer + " – " : ""}${c.name}`);
                              setUploadContactDropdown(false);
                            }}
                          >
                            {c.nummer && <strong style={{ marginRight: 8 }}>{c.nummer}</strong>}
                            {c.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Vorschau */}
                {uploadFile && !uploadPreview && !uploadSheetModal && (
                  <div className="r-form-row">
                    <button
                      className="r-btn ghost"
                      onClick={loadUploadPreview}
                      disabled={uploadPreviewLoading || uploadSheetLoading}
                    >
                      {uploadPreviewLoading ? "Lade Vorschau…" : <><Ico n="suche" /> Vorschau anzeigen</>}
                    </button>
                  </div>
                )}

                {uploadPreview && (
                  <div className="r-angebot-preview">
                    <div className="r-angebot-preview-header">
                      <strong>{uploadPreview.parsed?.titel || uploadPreview.parsed?.objekt || "Angebot"}</strong>
                      {uploadPreview.parsed?.angebot_nr && (
                        <span className="subtle"> · {uploadPreview.parsed.angebot_nr}</span>
                      )}
                      {uploadSchema === "v2" && <span style={{ marginLeft: 6, fontSize: "0.72rem", background: "var(--color-primary, #7c6af7)", color: "#fff", borderRadius: 4, padding: "1px 5px" }}>Schema 2</span>}
                      <span className="r-preview-stats">
                        {uploadPreview.positionen_count} Pos. · {uploadPreview.line_items_count} Zeilen
                      </span>
                    </div>
                    <div className="r-angebot-item-list">
                      {(uploadPreview.parsed?.items || []).slice(0, 25).map((item, i) => (
                        <div key={i} className={`r-angebot-item r-angebot-item-${item.type}`}>
                          {(item.type === "section" || item.type === "freitext") && (
                            <div className="r-angebot-section">{item.title || item.text}</div>
                          )}
                          {item.type === "los" && (
                            <div style={{ fontWeight: 700, color: "var(--color-primary, #7c6af7)", padding: "4px 0" }}>
                              ── {item.label}. {item.title} ──
                            </div>
                          )}
                          {(item.type === "item" || item.type === "artikel") && (
                            <div>
                              <div className="r-angebot-pos">
                                <span className="r-angebot-pos-nr">{item.pos}</span>
                                <span className="r-angebot-pos-desc">{item.desc?.slice(0, 80)}{item.desc?.length > 80 ? "…" : ""}</span>
                                {(item.price ?? item.einzelpreis) != null && (
                                  <span className="r-angebot-pos-price">
                                    {(item.price ?? item.einzelpreis).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                                  </span>
                                )}
                              </div>
                              {item.beschreibungstext && (
                                <div style={{ fontSize: "0.78rem", color: "var(--color-text-dim, #888)", paddingTop: 1, paddingLeft: 44 }}>{item.beschreibungstext.slice(0, 120)}</div>
                              )}
                            </div>
                          )}
                          {item.type === "zwischensumme" && (
                            <div style={{ fontStyle: "italic", color: "var(--color-text-dim, #888)", padding: "2px 0", textAlign: "right" }}>
                              {`── Zwischensumme - ${item.label || ""}${item.los_title ? `: ${item.los_title}` : ""}: ${item.gesamtpreis != null ? item.gesamtpreis.toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €" : "?"} ──`}
                            </div>
                          )}
                          {item.type === "text" && (
                            <div className="r-angebot-note subtle">{item.text?.slice(0, 60)}…</div>
                          )}
                        </div>
                      ))}
                      {(uploadPreview.parsed?.items || []).length > 25 && (
                        <div className="subtle" style={{ padding: "6px 0" }}>
                          … +{uploadPreview.parsed.items.length - 25} weitere Zeilen
                        </div>
                      )}

                      {/* Abschlussblöcke Preview */}
                      {uploadZusammenfassung && (() => {
                        const items = uploadPreview.parsed?.items || [];
                        const zwi = items.filter(i => i.type === "zwischensumme" && i.label);
                        const entries = zwi.length > 0
                          ? zwi.map(i => ({ title: i.los_title || i.label, amt: i.gesamtpreis }))
                          : items.filter(i => i.type === "artikel" && i.gesamtpreis).map(i => ({ title: i.desc?.slice(0, 40), amt: i.gesamtpreis }));
                        if (!entries.length) return null;
                        const netto = items.filter(i => i.type === "artikel").reduce((s, i) => s + (i.gesamtpreis || 0), 0);
                        const mwstB = Math.round(netto * 19) / 100;
                        return (
                          <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4, fontSize: "0.8rem", fontFamily: "monospace" }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Zusammenfassung</div>
                            {entries.map((e, i) => (
                              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                <span>• {e.title}</span>
                                <span>{e.amt?.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                              </div>
                            ))}
                            <div style={{ borderTop: "1px solid var(--color-border, #444)", marginTop: 4, paddingTop: 4 }}>
                              <div style={{ display: "flex", justifyContent: "space-between" }}><span>Nettobetrag</span><span>{netto.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span></div>
                              <div style={{ display: "flex", justifyContent: "space-between" }}><span>zzgl. MwSt 19%</span><span>{mwstB.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span></div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}><span>Angebotspreis</span><span>{(netto + mwstB).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span></div>
                            </div>
                          </div>
                        );
                      })()}
                      {uploadZahlungen && uploadZahlungsTexte.length > 0 && (
                        <div style={{ marginTop: 6, padding: "8px 10px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4, fontSize: "0.8rem" }}>
                          <div style={{ fontWeight: 600, marginBottom: 3 }}>Zahlungsmodalitäten</div>
                          {uploadZahlungsTexte.map((t, i) => <div key={i}>{t}</div>)}
                        </div>
                      )}
                      {uploadAusfuehrung && uploadAusfuehrungText && (
                        <div style={{ marginTop: 6, padding: "8px 10px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4, fontSize: "0.8rem" }}>
                          <div style={{ fontWeight: 600, marginBottom: 3 }}>Ausführungszeit</div>
                          <div>{uploadAusfuehrungText}</div>
                        </div>
                      )}
                    </div>
                    {/* Standardtexte-Dropdown */}
                    {(textVorlagen.length > 0 || textVorlagenLoading) && (
                      <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--color-surface-2, #252535)", borderRadius: 6 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: "0.88rem" }}><Ico n="stift" /> Standardtext einfügen</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <select
                            value={selectedTextVorlage}
                            onChange={e => setSelectedTextVorlage(e.target.value)}
                            style={{ flex: "1 1 180px", padding: "3px 6px", fontSize: "0.82rem", background: "var(--color-surface, #1a1a2e)", color: "inherit", border: "1px solid var(--color-border, #333)", borderRadius: 4 }}
                          >
                            <option value="">— Vorlage wählen —</option>
                            {textVorlagen.map(tv => (
                              <option key={tv.id} value={tv.id}>{tv.name}</option>
                            ))}
                          </select>
                          <select
                            value={insertPosition}
                            onChange={e => setInsertPosition(e.target.value)}
                            style={{ padding: "3px 6px", fontSize: "0.82rem", background: "var(--color-surface, #1a1a2e)", color: "inherit", border: "1px solid var(--color-border, #333)", borderRadius: 4 }}
                          >
                            <option value="intro">Am Anfang</option>
                            <option value="end">Am Ende</option>
                          </select>
                          <button
                            className="r-btn ghost"
                            style={{ padding: "3px 10px", fontSize: "0.82rem" }}
                            onClick={() => {
                              const tv = textVorlagen.find(t => t.id === selectedTextVorlage);
                              setInsertedTexts(prev => [...prev, { id: Date.now().toString(), text: tv.content || tv.name, insertAfter: insertPosition }]);
                              setSelectedTextVorlage("");
                            }}
                          >+ Einfügen</button>
                        </div>
                        {insertedTexts.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            {insertedTexts.map(it => (
                              <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontSize: "0.8rem", padding: "3px 6px", background: "var(--color-surface, #1a1a2e)", borderRadius: 4 }}>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  [{it.insertAfter === "intro" ? "Anfang" : "Ende"}] {it.text.slice(0, 80)}
                                </span>
                                <button
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-dim, #888)", padding: "0 2px" }}
                                  onClick={() => setInsertedTexts(prev => prev.filter(x => x.id !== it.id))}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Zuordnung für ungegruppierte Artikel (Schema 2) */}
                    {uploadSchema === "v2" && (uploadPreview.parsed?.ungrouped_artikel || []).length > 0 && (
                      <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--color-surface-2, #252535)", borderRadius: 6 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: "0.88rem" }}>Ungegruppierte Artikel – Gruppe zuordnen</div>
                        {(() => {
                          const excelLosCount = uploadPreview.excel_los_count || 0;
                          const excelGroups = (uploadPreview.parsed?.items || [])
                            .filter(i => i.type === "los")
                            .map(i => i.label);
                          const maxNum = Math.max(0, ...Object.values(uploadLosGroups).filter(v => typeof v === "number" && v > 0));
                          const losSums = {};
                          const losExcelSums = {};
                          (uploadPreview.parsed?.ungrouped_artikel || []).forEach(g => {
                            const val = uploadLosGroups[g.group_key];
                            const sum = (g.artikel || []).reduce((acc, a) => acc + (a.gesamtpreis || 0), 0);
                            if (typeof val === "number" && val > 0) losSums[val] = (losSums[val] || 0) + sum;
                            else if (typeof val === "string") losExcelSums[val] = (losExcelSums[val] || 0) + sum;
                          });
                          const getNewLabel = (num) => String.fromCharCode(65 + excelLosCount + num - 1);
                          return (
                            <>
                              {uploadPreview.parsed.ungrouped_artikel.map(g => {
                                const curVal = uploadLosGroups[g.group_key] ?? null;
                                return (
                                  <div key={g.group_key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: "0.85rem" }}>
                                    <div style={{ flex: 1 }}>
                                      <span style={{ fontWeight: 500 }}>{g.group_title || `Gruppe ${g.group_key}`}</span>
                                      <span className="subtle" style={{ marginLeft: 6 }}>({g.artikel.length} Artikel)</span>
                                    </div>
                                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                      <button
                                        className={`r-btn ${!curVal ? "primary" : "ghost"}`}
                                        style={{ padding: "2px 8px", fontSize: "0.78rem" }}
                                        onClick={() => setUploadLosGroups(prev => ({ ...prev, [g.group_key]: null }))}
                                      >–</button>
                                      {excelGroups.map(lbl => (
                                        <button
                                          key={`ex_${lbl}`}
                                          className={`r-btn ${curVal === `ex_${lbl}` ? "primary" : "ghost"}`}
                                          style={{ padding: "2px 8px", fontSize: "0.78rem", fontWeight: curVal === `ex_${lbl}` ? 700 : 400 }}
                                          onClick={() => setUploadLosGroups(prev => ({ ...prev, [g.group_key]: curVal === `ex_${lbl}` ? null : `ex_${lbl}` }))}
                                          title={`Zu Gruppe ${lbl} hinzufügen`}
                                        >{lbl}</button>
                                      ))}
                                      {Array.from({ length: maxNum + 1 }, (_, i) => i + 1).map(n => (
                                        <button
                                          key={n}
                                          className={`r-btn ${curVal === n ? "primary" : "ghost"}`}
                                          style={{ padding: "2px 8px", fontSize: "0.78rem", fontWeight: curVal === n ? 700 : 400 }}
                                          onClick={() => setUploadLosGroups(prev => ({ ...prev, [g.group_key]: curVal === n ? null : n }))}
                                        >{getNewLabel(n)}</button>
                                      ))}
                                      <button
                                        className="r-btn ghost"
                                        style={{ padding: "2px 8px", fontSize: "0.78rem" }}
                                        onClick={() => setUploadLosGroups(prev => ({ ...prev, [g.group_key]: maxNum + 1 }))}
                                        title="Neue Gruppe erstellen"
                                      >+ {getNewLabel(maxNum + 1)}</button>
                                    </div>
                                  </div>
                                );
                              })}
                              {(Object.keys(losSums).length > 0 || Object.keys(losExcelSums).length > 0) && (
                                <div style={{ marginTop: 10, borderTop: "1px solid var(--color-border, #333)", paddingTop: 8, fontSize: "0.82rem" }}>
                                  {Object.keys(losExcelSums).sort().map(lbl => (
                                    <div key={lbl} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                      <span>Zwischensumme {lbl.replace("ex_", "")}</span>
                                      <span style={{ fontWeight: 600 }}>{losExcelSums[lbl].toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                                    </div>
                                  ))}
                                  {Object.keys(losSums).sort((a,b) => a-b).map(n => (
                                    <div key={n} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                      <span>Zwischensumme {getNewLabel(Number(n))}</span>
                                      <span style={{ fontWeight: 600 }}>{losSums[n].toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Upload-Aktionen */}

                {/* ── Abschlussblöcke ───────────────────────────── */}
                {uploadSchema === "v2" && (
                  <div style={{ marginTop: 14, borderTop: "1px solid var(--color-border, #333)", paddingTop: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: "0.9rem" }}>Abschlussblöcke</div>

                    {/* Zusammenfassung */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: "0.88rem" }}>
                      <input type="checkbox" checked={uploadZusammenfassung} onChange={e => setUploadZusammenfassung(e.target.checked)} />
                      Zusammenfassung (Zwischensummen + Netto/MwSt/Gesamt)
                    </label>

                    {/* Zahlungsmodalitäten */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer", fontSize: "0.88rem" }}>
                      <input type="checkbox" checked={uploadZahlungen} onChange={e => setUploadZahlungen(e.target.checked)} />
                      Zahlungsmodalitäten
                    </label>
                    {uploadZahlungen && (
                      <div style={{ marginLeft: 24, marginBottom: 8 }}>
                        {uploadZahlungsTexte.map((t, i) => (
                          <div key={i} style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                            <input
                              value={t}
                              onChange={e => setUploadZahlungsTexte(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                              style={{ flex: 1, fontSize: "0.82rem", padding: "3px 6px", background: "var(--color-bg2, #1a1a1a)", color: "var(--color-text, #eee)", border: "1px solid var(--color-border, #444)", borderRadius: 4 }}
                            />
                            <button className="r-btn ghost" style={{ padding: "2px 7px", fontSize: "0.78rem" }} onClick={() => setUploadZahlungsTexte(prev => prev.filter((_, j) => j !== i))}><Ico n="schliessen" /></button>
                          </div>
                        ))}
                        <button className="r-btn ghost" style={{ fontSize: "0.8rem", padding: "3px 10px" }} onClick={() => setUploadZahlungsTexte(prev => [...prev, ""])}>+ Zeile</button>
                      </div>
                    )}

                    {/* Ausführungszeit */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer", fontSize: "0.88rem" }}>
                      <input type="checkbox" checked={uploadAusfuehrung} onChange={e => setUploadAusfuehrung(e.target.checked)} />
                      Ausführungszeit
                    </label>
                    {uploadAusfuehrung && (
                      <input
                        value={uploadAusfuehrungText}
                        onChange={e => setUploadAusfuehrungText(e.target.value)}
                        placeholder="Ca. drei Wochen nach Auftragserteilung"
                        style={{ marginLeft: 24, width: "calc(100% - 24px)", fontSize: "0.82rem", padding: "4px 8px", background: "var(--color-bg2, #1a1a1a)", color: "var(--color-text, #eee)", border: "1px solid var(--color-border, #444)", borderRadius: 4, marginBottom: 4 }}
                      />
                    )}
                  </div>
                )}

                {uploadError && <div className="r-error">{uploadError}</div>}

                <div className="r-modal-actions" style={{ marginTop: 14 }}>
                  <button className="r-btn ghost" onClick={() => setUploadModal(false)} disabled={uploadLoading}>Abbrechen</button>
                  <button
                    className="r-btn angebot-excel"
                    onClick={createUploadAngebot}
                    disabled={uploadLoading || !uploadFile || !uploadContactId}
                  >
                    {uploadLoading ? "Erstelle Angebot…" : <><Ico n="diagramm" /> Angebot in Lexware erstellen</>}
                  </button>
                </div>
              </>
            ) : (
              <div className="r-modal-success">
                <div className="r-success-icon"><Ico n="haken" gr={40} /></div>
                <div><strong>Angebot erstellt!</strong></div>
                {uploadResult.angebot_nr && <div className="subtle">{uploadResult.angebot_nr}</div>}
                {uploadResult.titel && <div>{uploadResult.titel}</div>}
                <div className="subtle">{uploadResult.positionen_count} Positionen · {uploadResult.datum}</div>
                {uploadResult.old_quotation_id && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "#fff3cd", borderRadius: 6, fontSize: 13, color: "#856404" }}>
                    <Ico n="warnung" /> Neues Angebot erstellt (Lexware API unterstützt kein Überschreiben).
                    {uploadResult.old_voucher_number && <> Bitte <strong>{uploadResult.old_voucher_number}</strong> manuell in Lexware löschen.</>}
                    {uploadResult.old_lexware_url && (
                      <> <a href={uploadResult.old_lexware_url} target="_blank" rel="noreferrer" style={{ color: "#856404" }}>→ Altes Angebot öffnen</a></>
                    )}
                  </div>
                )}
                {uploadResult.lexware_url && (
                  <a href={uploadResult.lexware_url} target="_blank" rel="noreferrer" className="r-btn primary" style={{ marginTop: 12 }}>
                    → Neues Angebot in Lexware öffnen
                  </a>
                )}
                <button className="r-btn ghost" onClick={() => setUploadModal(false)} style={{ marginTop: 8 }}>Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ Fremdes Excel → Schema 2 (Prompt + Mapping) ═══════ */}
      {fremdModal && (
        <FremdExcelModal
          source={fremdModal.source}
          sheet={fremdModal.sheet}
          initialMode={fremdModal.mode || "mapping"}
          onClose={() => setFremdModal(null)}
          allContacts={allContacts}
          loadAllContacts={loadAllContacts}
        />
      )}

      {/* ═══════════════ TAB: CONFIG ═══════════════ */}
      {tab === "config" && (
        <div className="r-section">
          <h2>Konfiguration</h2>
          <div className="r-config-list">
            <div className={`r-config-item ${config?.lexware ? "ok" : "missing"}`}>
              <span className="r-config-status"><Ico n={config?.lexware ? "haken" : "schliessen"} /></span>
              <div>
                <strong>Lexware API-Key</strong>
                <div className="subtle">LEXWARE_API_KEY in backend/.env</div>
                {!config?.lexware && (
                  <a href="https://app.lexware.de/addons/public-api" target="_blank" rel="noreferrer" className="r-link">
                    → API-Key generieren (kostenlos im Abo enthalten)
                  </a>
                )}
              </div>
            </div>
            <div className={`r-config-item ${config?.trello ? "ok" : "missing"}`}>
              <span className="r-config-status"><Ico n={config?.trello ? "haken" : "schliessen"} /></span>
              <div>
                <strong>Trello-Credentials</strong>
                <div className="subtle">TRELLO_API_KEY, TRELLO_TOKEN, TRELLO_BOARD_ID in backend/.env</div>
              </div>
            </div>
            <div className="r-config-item ok">
              <span className="r-config-status">ℹ</span>
              <div>
                <strong>Rechnungs-Label</strong>
                <div>"{config?.rechnung_label || "Rechnung schreiben"}"</div>
                <div className="subtle">Änderbar via TRELLO_RECHNUNG_LABEL in backend/.env</div>
              </div>
            </div>
          </div>
          <div className="r-info-box">
            <h3>Wie es funktioniert</h3>
            <ol>
              <li>Trello-Cards mit Label <strong>"{config?.rechnung_label}"</strong> werden hier angezeigt</li>
              <li>Aus dem Kartentitel (z.B. <code>05A - Dachrinne leckt</code>) werden Prefix und Beschreibung erkannt</li>
              <li>Mit „Entwurf“ wird ein Rechnungsentwurf in Lexware Office erstellt – mit Bildern als Anhang-PDF</li>
              <li>Du öffnest den Entwurf in Lexware, prüfst ihn und finalisierst ihn dort</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Fremdes Excel → Schema 2: KI-Prompt zum Kopieren + manuelles Mapping.
// source = { file } (Upload) oder { card_id, attachment_id } (Trello).
// Keine KI-/API-Aufrufe im Programm — der Prompt wird extern benutzt.
// ═══════════════════════════════════════════════════════════════════════════
function FremdExcelModal({ source, sheet, initialMode, onClose, allContacts, loadAllContacts }) {
  const [mode, setMode] = useState(initialMode || "mapping"); // "mapping" | "prompt"

  // KI-Prompt
  const [prompt, setPrompt] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptMsg, setPromptMsg] = useState("");

  // Mapping
  const [mappings, setMappings] = useState([]);
  const [mappingId, setMappingId] = useState("");
  const [mapName, setMapName] = useState("");
  const [grid, setGrid] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [bloecke, setBloecke] = useState([]);
  const [freitexte, setFreitexte] = useState("");
  const [items, setItems] = useState([]);          // editierbare v2-Items nach Transform
  const [transformInfo, setTransformInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  // Erstellen
  const [contactId, setContactId] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [contactDropdown, setContactDropdown] = useState(false);
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const sourceFormData = useCallback(() => {
    const fd = new FormData();
    if (source.file) fd.append("file", source.file);
    else {
      fd.append("card_id", source.card_id);
      fd.append("attachment_id", source.attachment_id);
    }
    return fd;
  }, [source]);

  const emptyBlock = () => ({
    titel: "", typ: "artikel", zeile_von: "", zeile_bis: "",
    spalten: { bezeichnung: "A", menge: "", einheit: "", einzelpreis: "", gesamtpreis: "", beschreibung: "" },
    nur_gp_als_pauschal: true, stundensatz: "67",
    skip_keywords: "summe, kosten ca, gesamt, tag ",
  });

  const loadGrid = useCallback(async (sheetName) => {
    setGridLoading(true); setError("");
    try {
      const fd = sourceFormData();
      if (sheetName) fd.append("sheet_name", sheetName);
      const r = await fetch(`${API_BASE}/rechnungen/angebot-excel-grid`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setGrid(d);
    } catch (e) { setError(e.message); }
    finally { setGridLoading(false); }
  }, [sourceFormData]);

  const loadMappings = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/rechnungen/angebot-excel-mappings`);
      if (r.ok) {
        const d = await r.json();
        setMappings(d.mappings || []);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadMappings();
    loadGrid(sheet || "");
    setBloecke([emptyBlock()]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prompt lazy laden
  useEffect(() => {
    if (mode !== "prompt" || prompt || promptLoading) return;
    setPromptLoading(true);
    fetch(`${API_BASE}/rechnungen/angebot-ki-prompt`)
      .then(r => r.json())
      .then(d => setPrompt(d.prompt || ""))
      .catch(() => setPromptMsg("Prompt konnte nicht geladen werden"))
      .finally(() => setPromptLoading(false));
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePrompt = async () => {
    setPromptMsg("");
    try {
      const r = await fetch(`${API_BASE}/rechnungen/angebot-ki-prompt`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });
      if (!r.ok) throw new Error(`Fehler ${r.status}`);
      setPromptMsg("Gespeichert");
    } catch (e) { setPromptMsg("Speichern fehlgeschlagen: " + e.message); }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setPromptMsg("In Zwischenablage kopiert");
    } catch (_) { setPromptMsg("Kopieren fehlgeschlagen — Text manuell markieren"); }
  };

  const buildConfig = useCallback(() => ({
    sheet: grid?.sheet || sheet || undefined,
    bloecke: bloecke
      .filter(b => b.zeile_von && b.zeile_bis && b.spalten.bezeichnung)
      .map(b => ({
        titel: b.titel || (b.typ === "zeitaufwand" ? "Arbeitszeit" : "Material"),
        typ: b.typ,
        zeile_von: parseInt(b.zeile_von, 10),
        zeile_bis: parseInt(b.zeile_bis, 10),
        spalten: Object.fromEntries(
          Object.entries(b.spalten).map(([k, v]) => [k, (v || "").trim().toUpperCase() || null])
        ),
        nur_gp_als_pauschal: b.typ === "artikel" ? !!b.nur_gp_als_pauschal : false,
        stundensatz: b.typ === "zeitaufwand"
          ? (parseFloat(String(b.stundensatz).replace(",", ".")) || 67)
          : null,
        skip_keywords: b.skip_keywords.split(",").map(s => s.trim().toLowerCase()).filter(Boolean),
      })),
    freitexte: freitexte.split("\n").map(s => s.trim()).filter(Boolean),
  }), [grid, sheet, bloecke, freitexte]);

  const runTransform = async () => {
    setBusy(true); setError(""); setItems([]); setTransformInfo(null);
    try {
      const cfg = buildConfig();
      if (!cfg.bloecke.length) throw new Error("Mindestens einen Block mit Zeilen + Bezeichnungs-Spalte angeben");
      const fd = sourceFormData();
      fd.append("config", JSON.stringify(cfg));
      const r = await fetch(`${API_BASE}/rechnungen/angebot-mapping-transform`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setItems((d.parsed?.items || []).map(x => ({ ...x })));
      setTransformInfo({ positionen: d.positionen_count, netto: d.netto_summe });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const saveMapping = async () => {
    setSaveMsg("");
    const name = mapName.trim();
    if (!name) { setSaveMsg("Name fehlt"); return; }
    try {
      const cfg = buildConfig();
      const isUpdate = mappingId && mappings.some(m => String(m.id) === String(mappingId) && m.name === name);
      const url = isUpdate
        ? `${API_BASE}/rechnungen/angebot-excel-mappings/${mappingId}`
        : `${API_BASE}/rechnungen/angebot-excel-mappings`;
      const r = await fetch(url, {
        method: isUpdate ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, config: cfg }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setSaveMsg("Mapping gespeichert");
      if (d.id) setMappingId(String(d.id));
      loadMappings();
    } catch (e) { setSaveMsg("Speichern fehlgeschlagen: " + e.message); }
  };

  const applyMapping = (m) => {
    setMappingId(String(m.id));
    setMapName(m.name);
    const cfg = m.config || {};
    setBloecke((cfg.bloecke || []).map(b => ({
      titel: b.titel || "",
      typ: b.typ || "artikel",
      zeile_von: String(b.zeile_von || ""),
      zeile_bis: String(b.zeile_bis || ""),
      spalten: {
        bezeichnung: b.spalten?.bezeichnung || "",
        menge: b.spalten?.menge || "",
        einheit: b.spalten?.einheit || "",
        einzelpreis: b.spalten?.einzelpreis || "",
        gesamtpreis: b.spalten?.gesamtpreis || "",
        beschreibung: b.spalten?.beschreibung || "",
      },
      nur_gp_als_pauschal: b.nur_gp_als_pauschal !== false,
      stundensatz: String(b.stundensatz ?? "67"),
      skip_keywords: (b.skip_keywords || []).join(", "),
    })));
    setFreitexte((cfg.freitexte || []).join("\n"));
    if (cfg.sheet && grid && cfg.sheet !== grid.sheet) loadGrid(cfg.sheet);
  };

  const updateBlock = (i, patch) =>
    setBloecke(prev => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const updateBlockSpalte = (i, key, val) =>
    setBloecke(prev => prev.map((b, j) => (j === i ? { ...b, spalten: { ...b.spalten, [key]: val } } : b)));

  const updateItem = (i, patch) => {
    setItems(prev => prev.map((it, j) => {
      if (j !== i) return it;
      const next = { ...it, ...patch };
      if (next.type === "artikel") {
        const m = parseFloat(String(next.menge ?? "").toString().replace(",", "."));
        const ep = parseFloat(String(next.einzelpreis ?? "").toString().replace(",", "."));
        if (!isNaN(m) && !isNaN(ep)) next.gesamtpreis = Math.round(m * ep * 100) / 100;
      }
      return next;
    }));
  };

  const nettoSumme = useMemo(() => {
    let sum = 0;
    items.forEach(it => {
      if (it.type !== "artikel") return;
      const m = parseFloat(String(it.menge ?? "1").toString().replace(",", ".")) || 1;
      const ep = parseFloat(String(it.einzelpreis ?? "").toString().replace(",", "."));
      if (!isNaN(ep) && ep > 0) sum += Math.round(ep * 100) / 100 * m;
    });
    return Math.round(sum * 100) / 100;
  }, [items]);

  const createAngebot = async () => {
    if (!contactId || !items.length) return;
    setCreating(true); setError("");
    try {
      const payload = {
        items: items.map(it => ({
          ...it,
          menge: it.menge === "" ? null : it.menge,
          einzelpreis: it.einzelpreis === "" ? null : it.einzelpreis,
          gesamtpreis: it.gesamtpreis === "" ? null : it.gesamtpreis,
        })),
        prolog: "",
        lexware_contact_id: contactId,
        datum,
        mwst: 19.0,
        include_zusammenfassung: true,
      };
      if (source.card_id) payload.card_id = source.card_id;
      const r = await fetch(`${API_BASE}/rechnungen/angebot-aus-daten`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setResult(d);
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter(c => {
      const nr = String(c.nummer ?? "").toLowerCase();
      const name = (c.name || "").toLowerCase();
      return nr.includes(q) || name.includes(q);
    });
  }, [allContacts, contactSearch]);

  const colLetters = useMemo(() => {
    const maxIdx = grid?.max_col && grid.max_col.length === 1
      ? grid.max_col.charCodeAt(0) - 64
      : 26;
    return Array.from({ length: Math.min(maxIdx, 26) }, (_, i) => String.fromCharCode(65 + i));
  }, [grid]);

  const fmtEur = (v) => (v == null ? "–" : Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €");
  const inputStyle = { fontSize: "0.8rem", padding: "3px 6px", background: "var(--color-bg2, #1a1a1a)", color: "var(--color-text, #eee)", border: "1px solid var(--color-border, #444)", borderRadius: 4 };

  return (
    <div className="r-modal-overlay" style={{ zIndex: 1200 }} onClick={() => !busy && !creating && onClose()}>
      <div className="r-modal" style={{ maxWidth: 940, width: "min(94vw, 940px)" }} onClick={e => e.stopPropagation()}>
        <div className="r-modal-header">
          <h3>Fremdes Excel → Schema 2</h3>
          <button className="r-modal-close" onClick={onClose}><Ico n="schliessen" /></button>
        </div>

        {result ? (
          <div className="r-modal-success">
            <div><strong>Angebot erstellt!</strong></div>
            {result.angebot_nr && <div className="subtle">{result.angebot_nr}</div>}
            <div className="subtle">{result.positionen_count} Positionen · {result.datum}</div>
            {result.lexware_url && (
              <a href={result.lexware_url} target="_blank" rel="noreferrer" className="r-btn primary" style={{ marginTop: 12 }}>
                → Angebot in Lexware öffnen
              </a>
            )}
            <button className="r-btn ghost" onClick={onClose} style={{ marginTop: 8 }}>Schließen</button>
          </div>
        ) : (
          <>
            {/* Modus-Umschalter */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button className={`r-btn ${mode === "mapping" ? "primary" : "ghost"}`} onClick={() => setMode("mapping")}>Selbst mappen</button>
              <button className={`r-btn ${mode === "prompt" ? "primary" : "ghost"}`} onClick={() => setMode("prompt")}>KI-Prompt kopieren</button>
            </div>

            {mode === "prompt" && (
              <div>
                <div className="subtle" style={{ marginBottom: 8, fontSize: "0.85rem", lineHeight: 1.5 }}>
                  1. Prompt kopieren → 2. zusammen mit der Excel-Datei in einen KI-Chat (ChatGPT/Claude) einfügen →
                  3. die erzeugte <strong>Schema-2-Excel</strong> herunterladen → 4. hier ganz normal über
                  „Angebot erstellen" hochladen (wird automatisch als Schema 2 erkannt).
                </div>
                {promptLoading ? (
                  <div className="subtle">Prompt wird geladen…</div>
                ) : (
                  <textarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    rows={16}
                    style={{ ...inputStyle, width: "100%", fontFamily: "monospace", fontSize: "0.78rem", lineHeight: 1.4 }}
                  />
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                  <button className="r-btn primary" onClick={copyPrompt} disabled={!prompt}>Kopieren</button>
                  <button className="r-btn ghost" onClick={savePrompt} disabled={!prompt}>Prompt speichern</button>
                  {promptMsg && <span className="subtle" style={{ fontSize: "0.82rem" }}>{promptMsg}</span>}
                </div>
              </div>
            )}

            {mode === "mapping" && (
              <div>
                {/* Gespeicherte Mappings */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <select
                    value={mappingId}
                    onChange={e => {
                      const m = mappings.find(x => String(x.id) === e.target.value);
                      if (m) applyMapping(m);
                      else { setMappingId(""); setMapName(""); }
                    }}
                    style={{ ...inputStyle, minWidth: 200 }}
                  >
                    <option value="">— gespeichertes Mapping wählen —</option>
                    {mappings.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <input
                    value={mapName}
                    onChange={e => setMapName(e.target.value)}
                    placeholder="Mapping-Name (zum Speichern)"
                    style={{ ...inputStyle, flex: "1 1 180px" }}
                  />
                  <button className="r-btn ghost" style={{ fontSize: "0.82rem" }} onClick={saveMapping}>Speichern</button>
                  {mappingId && (
                    <button
                      className="r-btn ghost" style={{ fontSize: "0.82rem" }}
                      onClick={async () => {
                        await fetch(`${API_BASE}/rechnungen/angebot-excel-mappings/${mappingId}`, { method: "DELETE" });
                        setMappingId(""); setMapName(""); loadMappings();
                      }}
                    >Löschen</button>
                  )}
                  {saveMsg && <span className="subtle" style={{ fontSize: "0.8rem" }}>{saveMsg}</span>}
                </div>

                {/* Sheet-Auswahl + Grid */}
                {grid && grid.sheets?.length > 1 && (
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: "0.82rem", marginRight: 8 }}>Sheet:</label>
                    <select value={grid.sheet} onChange={e => loadGrid(e.target.value)} style={inputStyle}>
                      {grid.sheets.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                {gridLoading && <div className="subtle">Excel wird gelesen…</div>}
                {grid && !gridLoading && (
                  <div style={{ maxHeight: 240, overflow: "auto", border: "1px solid var(--color-border, #333)", borderRadius: 6, marginBottom: 12 }}>
                    <table style={{ borderCollapse: "collapse", fontSize: "0.74rem", minWidth: "100%" }}>
                      <thead>
                        <tr>
                          <th style={{ position: "sticky", top: 0, background: "var(--color-surface-2, #252535)", padding: "3px 6px", textAlign: "right" }}>#</th>
                          {colLetters.map(l => (
                            <th key={l} style={{ position: "sticky", top: 0, background: "var(--color-surface-2, #252535)", padding: "3px 8px", textAlign: "left" }}>{l}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {grid.rows.map(r => (
                          <tr key={r.row}>
                            <td style={{ padding: "2px 6px", textAlign: "right", color: "var(--color-text-dim, #888)", borderRight: "1px solid var(--color-border, #333)" }}>{r.row}</td>
                            {colLetters.map(l => (
                              <td key={l} style={{ padding: "2px 8px", whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {r.cells[l] || ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Block-Editor */}
                {bloecke.map((b, i) => (
                  <div key={i} style={{ marginBottom: 10, padding: "10px 12px", background: "var(--color-surface-2, #252535)", borderRadius: 6, border: "1px solid var(--color-border, #333)" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
                      <strong style={{ fontSize: "0.85rem" }}>Block {i + 1}</strong>
                      <input value={b.titel} onChange={e => updateBlock(i, { titel: e.target.value })} placeholder={b.typ === "zeitaufwand" ? "Arbeitszeit" : "Material"} style={{ ...inputStyle, width: 140 }} />
                      <select value={b.typ} onChange={e => updateBlock(i, { typ: e.target.value })} style={inputStyle}>
                        <option value="artikel">Artikel (Material)</option>
                        <option value="zeitaufwand">Zeitaufwand (Std)</option>
                      </select>
                      <span style={{ fontSize: "0.8rem" }}>Zeile</span>
                      <input value={b.zeile_von} onChange={e => updateBlock(i, { zeile_von: e.target.value })} placeholder="von" style={{ ...inputStyle, width: 55 }} />
                      <input value={b.zeile_bis} onChange={e => updateBlock(i, { zeile_bis: e.target.value })} placeholder="bis" style={{ ...inputStyle, width: 55 }} />
                      {bloecke.length > 1 && (
                        <button className="r-btn ghost" style={{ padding: "2px 8px", fontSize: "0.78rem", marginLeft: "auto" }} onClick={() => setBloecke(prev => prev.filter((_, j) => j !== i))}><Ico n="schliessen" /></button>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", fontSize: "0.8rem" }}>
                      {[["bezeichnung", "Bezeichnung"], ["menge", "Menge"], ["einheit", "Einheit"], ["einzelpreis", "EP"], ["gesamtpreis", "GP"], ["beschreibung", "Beschr."]].map(([key, lbl]) => (
                        (b.typ === "artikel" || ["bezeichnung", "menge", "beschreibung"].includes(key)) && (
                          <label key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {lbl}
                            <select value={b.spalten[key] || ""} onChange={e => updateBlockSpalte(i, key, e.target.value)} style={{ ...inputStyle, width: 52 }}>
                              <option value="">–</option>
                              {colLetters.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                          </label>
                        )
                      ))}
                      {b.typ === "zeitaufwand" && (
                        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          Stundensatz €
                          <input value={b.stundensatz} onChange={e => updateBlock(i, { stundensatz: e.target.value })} style={{ ...inputStyle, width: 60 }} />
                        </label>
                      )}
                      {b.typ === "artikel" && (
                        <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                          <input type="checkbox" checked={b.nur_gp_als_pauschal} onChange={e => updateBlock(i, { nur_gp_als_pauschal: e.target.checked })} />
                          Nur-GP-Zeilen als Pauschal
                        </label>
                      )}
                    </div>
                    <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem" }}>
                      <span>Überspringen wenn Bezeichnung enthält:</span>
                      <input value={b.skip_keywords} onChange={e => updateBlock(i, { skip_keywords: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <button className="r-btn ghost" style={{ fontSize: "0.82rem" }} onClick={() => setBloecke(prev => [...prev, { ...emptyBlock(), typ: "zeitaufwand", spalten: { ...emptyBlock().spalten } }])}>+ Block</button>
                  <label style={{ fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                    Freitext-Zeilen (je Zeile):
                    <input value={freitexte} onChange={e => setFreitexte(e.target.value)} placeholder="Zuzüglich Fahrtkosten" style={{ ...inputStyle, flex: 1 }} />
                  </label>
                  <button className="r-btn primary" onClick={runTransform} disabled={busy || gridLoading}>
                    {busy ? "Transformiere…" : "Vorschau berechnen"}
                  </button>
                </div>

                {/* Editierbare Vorschau */}
                {items.length > 0 && (
                  <div style={{ border: "1px solid var(--color-border, #333)", borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "0.85rem" }}>
                      <strong>Vorschau ({transformInfo?.positionen ?? 0} Positionen) — editierbar</strong>
                      <span>Netto: <strong>{fmtEur(nettoSumme)}</strong></span>
                    </div>
                    <div style={{ maxHeight: 300, overflowY: "auto" }}>
                      {items.map((it, i) => (
                        <div key={i} style={{ marginBottom: 3 }}>
                          {it.type === "los" && (
                            <div style={{ fontWeight: 700, color: "var(--color-primary, #7c6af7)", padding: "4px 0" }}>── {it.label}. {it.title} ──</div>
                          )}
                          {it.type === "artikel" && (
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span style={{ width: 40, fontSize: "0.75rem", color: "var(--color-text-dim, #888)" }}>{it.pos}</span>
                              <input value={it.desc || ""} onChange={e => updateItem(i, { desc: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                              <input value={it.menge ?? ""} onChange={e => updateItem(i, { menge: e.target.value })} title="Menge" style={{ ...inputStyle, width: 64, textAlign: "right" }} />
                              <input value={it.einheit ?? ""} onChange={e => updateItem(i, { einheit: e.target.value })} title="Einheit" style={{ ...inputStyle, width: 70 }} />
                              <input value={it.einzelpreis ?? ""} onChange={e => updateItem(i, { einzelpreis: e.target.value })} title="Einzelpreis €" style={{ ...inputStyle, width: 76, textAlign: "right" }} />
                              <span style={{ width: 86, textAlign: "right", fontSize: "0.8rem" }}>{fmtEur(it.gesamtpreis)}</span>
                              <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-dim, #888)" }} title="Zeile entfernen" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}><Ico n="schliessen" /></button>
                            </div>
                          )}
                          {it.type === "artikel" && it.beschreibungstext && (
                            <div style={{ fontSize: "0.74rem", color: "var(--color-text-dim, #888)", paddingLeft: 46 }}>{it.beschreibungstext}</div>
                          )}
                          {it.type === "zwischensumme" && (
                            <div style={{ fontStyle: "italic", color: "var(--color-text-dim, #888)", textAlign: "right", padding: "2px 0" }}>── Zwischensumme {it.los_title || it.label} ──</div>
                          )}
                          {it.type === "freitext" && (
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span className="subtle" style={{ flex: 1, fontSize: "0.8rem" }}>{it.text}</span>
                              <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-dim, #888)" }} onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}><Ico n="schliessen" /></button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Erstellen */}
                    <div style={{ borderTop: "1px solid var(--color-border, #333)", marginTop: 10, paddingTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                      <label style={{ fontSize: "0.82rem" }}>
                        Datum<br />
                        <input type="date" value={datum} onChange={e => setDatum(e.target.value)} style={inputStyle} />
                      </label>
                      <div style={{ position: "relative", flex: "1 1 220px" }}>
                        <label style={{ fontSize: "0.82rem" }}>Lexware-Kontakt {!contactId && <span className="r-hint-inline warn">erforderlich</span>}</label>
                        <input
                          value={contactSearch}
                          onChange={e => { setContactSearch(e.target.value); setContactId(""); setContactDropdown(true); }}
                          onFocus={() => { setContactDropdown(true); loadAllContacts(); }}
                          onBlur={() => setTimeout(() => setContactDropdown(false), 150)}
                          placeholder="Kundennummer oder Name…"
                          autoComplete="off"
                          style={{ ...inputStyle, width: "100%" }}
                        />
                        {contactDropdown && filteredContacts.length > 0 && (
                          <div style={{
                            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                            background: "var(--color-surface, #1e1e2e)",
                            border: "1px solid var(--color-border, #333)",
                            borderRadius: 6, maxHeight: 200, overflowY: "auto",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.4)"
                          }}>
                            {filteredContacts.slice(0, 30).map(c => (
                              <div
                                key={c.id}
                                style={{ padding: "6px 10px", cursor: "pointer", fontSize: "0.82rem" }}
                                onMouseDown={() => {
                                  setContactId(c.id);
                                  setContactSearch(`${c.nummer ? c.nummer + " – " : ""}${c.name}`);
                                  setContactDropdown(false);
                                }}
                              >
                                {c.nummer && <strong style={{ marginRight: 6 }}>{c.nummer}</strong>}
                                {c.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        className="r-btn angebot-excel"
                        onClick={createAngebot}
                        disabled={creating || !contactId || !items.length}
                      >
                        {creating ? "Erstelle Angebot…" : "Angebot in Lexware erstellen"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && <div className="r-error">{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}
